import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Button,
  Table,
  Badge,
  Modal,
  ModalBody,
  UncontrolledTooltip,
} from "reactstrap";
import { ClipLoader } from "react-spinners";
import api from "../../../services/api";
import { useCompanies } from "../../../hooks/companies";
import { formatDateLabel, toDateInputValue } from "../../../utils/dateOnly";
import { ehNfse, rotuloDocumento, rotuloDocumentos } from "../../../utils/modeloDocumento";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import PedirAnaliseModal, { MAX_CTES_POR_ENVIO } from "./components/PedirAnaliseModal";
import LimiteDivergenciaValue from "../../../components/cte/LimiteDivergenciaValue";
import TributoIncideBadge from "../../../components/cte/TributoIncideBadge";
import CanceladoSefazBadge, {
  CANCELADO_SEFAZ_TITLE,
  isCanceladoSefaz,
} from "../../../components/cte/CanceladoSefazBadge";
import FinancialComponentComparisonTable from "../../../components/cte/FinancialComponentComparisonTable";
import RecalcularFreteCteButton from "../../../components/cte/RecalcularFreteCteButton";
import VerXmlCteButton from "../../../components/cte/VerXmlCteButton";
import { FaturaDoCteAba, FaturaDoCteCelula, FaturaDoCteResumo } from "../../../components/cte/FaturaDoCte";
import "../../../components/cte/CteAuditDetailModal.css";
import { SimulatedPriceTablesValue, podeAbrirTabelaFrete } from "../../../components/cte/CteAuditDetailModal";
import TabelaFreteModal from "../TabelasFrete/TabelaFreteModal";
import NotaFiscalDetailModal from "../ConsultaNotasFiscais/NotaFiscalDetailModal";
import { FaturaDetalheModal } from "../ConferenciaFatura/modais";
import { useAuth } from "../../../hooks/auth";
import PageHero from "../../../components/PageHero";
import AvisoForaDoFiltro from "../../../components/AvisoForaDoFiltro";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotEyebrowStyle,
  autopilotInsetCardStyle,
  autopilotSecondaryButtonStyle,
  autopilotTitleStyle,
} from "../../../components/autopilot/styles";

// Referência ao documento em contexto com artigo e gênero: "este CT-e" x "esta
// NFse" (PO1-3299). Textos agregados (cards, menu) continuam como estão.
function refDocumento(modelo) {
  const rotulo = rotuloDocumento(modelo);
  const fem = ehNfse(modelo);
  return {
    rotulo,
    o: `${fem ? "a" : "o"} ${rotulo}`,
    O: `${fem ? "A" : "O"} ${rotulo}`,
    do: `${fem ? "da" : "do"} ${rotulo}`,
    ao: `${fem ? "à" : "ao"} ${rotulo}`,
    este: `${fem ? "esta" : "este"} ${rotulo}`,
    deste: `${fem ? "desta" : "deste"} ${rotulo}`,
    neste: `${fem ? "nesta" : "neste"} ${rotulo}`,
    cancelado: fem ? "cancelada" : "cancelado",
  };
}

function formatCurrency(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "-";
  const numeric = Number(value);
  const normalized = Math.abs(numeric) < 0.005 ? 0 : numeric;
  return normalized.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatWeight(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "-";
  return `${Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  })} kg`;
}

function formatBooleanLabel(value) {
  if (value === true || value === 1 || value === "1") return "Sim";
  if (value === false || value === 0 || value === "0") return "Não";
  return "-";
}

function formatPercent(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "-";
  return `${Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
}

function formatAuditValue(value, kind) {
  if (value === null || value === undefined || value === "") return "-";
  if (kind === "peso") return formatWeight(value);
  if (kind === "currency") return formatCurrency(value);
  return String(value);
}

function toNumberOrNull(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return null;
  return Number(value);
}

const getOptionValue = (option) => {
  if (!option) return null;
  return option.value ?? option.id ?? option.Id ?? option.transportadora_id ?? null;
};

function formatChaveAcesso(value) {
  if (!value) return "-";
  const s = String(value).replace(/\s/g, "");
  if (s.length <= 12) return s;
  return `...${s.slice(-12)}`;
}

function calcDivergencia(receber, simulado) {
  const r = Number(receber || 0);
  const s = Number(simulado || 0);
  if (s === 0) return null; // simulado=0 means not yet calculated
  return r - s;
}

// Etapa (card de status) de um CT-e, com os mesmos critérios do backend
// (STATUS_FILTERS de getCteAuditDetails): situação 2 = sem divergência,
// 3 com simulado > 0 = com divergência (ou aceito, quando o CT-e está OK na
// conferência da fatura, PO1-3367), 1 ou 3 com simulado 0 = pendente de
// cálculo. Usado pela busca global para apontar onde o CT-e está.
function getStatusBucket(item) {
  const situacao = Number(item?.situacao);
  const simulado = Number(item?.valorFreteSimulado || 0);
  if (situacao === 2) return "semDivergencia";
  if (situacao === 3 && simulado > 0) return item?.aceitoNaFatura ? "aceitoDivergencia" : "divergencia";
  if (situacao === 3) return "naoCalculado";
  if (situacao === 1) return "naoCalculado";
  return null;
}

// Aprovação manual (PO1-1801): situação OK com divergência ACIMA da aceitável
// do cliente só existe por aprovação humana — o cálculo nunca marca OK acima
// do limite (e, dentro do limite, iguala o simulado ao receber). O backend já
// devolve `divergenciaAceitavel` por CT-e = max(receber × %param/100, valor
// absoluto), mesma fórmula do card do detalhe (PO1-2496).
function isAprovadoManualmente(item) {
  if (Number(item?.situacao) !== 2) return false;
  const delta = calcDivergencia(item?.valorFreteReceber, item?.valorFreteSimulado);
  if (delta === null || delta === 0) return false;
  const tolerancia = Number(item?.divergenciaAceitavel);
  return Math.abs(delta) > (Number.isFinite(tolerancia) ? tolerancia : 0);
}

const DATE_SHORTCUTS = [
  {
    label: "7 dias",
    getDates: () => {
      const hoje = new Date();
      const de = new Date();
      de.setDate(hoje.getDate() - 6);
      return {
        de: toDateInputValue(de),
        ate: toDateInputValue(hoje),
      };
    },
  },
  {
    label: "30 dias",
    getDates: () => {
      const hoje = new Date();
      const de = new Date();
      de.setDate(hoje.getDate() - 29);
      return {
        de: toDateInputValue(de),
        ate: toDateInputValue(hoje),
      };
    },
  },
  {
    label: "Mês atual",
    getDates: () => {
      const hoje = new Date();
      const de = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const ate = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      return {
        de: toDateInputValue(de),
        ate: toDateInputValue(ate),
      };
    },
  },
  {
    label: "Mês anterior",
    getDates: () => {
      const hoje = new Date();
      const de = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
      const ate = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
      return {
        de: toDateInputValue(de),
        ate: toDateInputValue(ate),
      };
    },
  },
];

const STATUS_META = {
  naoCalculado: {
    title: "Pendente de cálculo",
    short: "CTe sem valor simulado válido após tentativa de processamento",
    color: "var(--everlog-status-warning)",
    detailTitle: "Pendente de cálculo",
    detailDescription: "Corrija a regra de frete ou reprocesse os documentos pendentes.",
  },
  divergencia: {
    title: "Com divergência",
    short: "Diferença entre o valor cobrado e o valor simulado",
    color: "var(--everlog-status-danger)",
    detailTitle: "Com divergência",
    detailDescription: "Revise cobrança, tabela e consistência do cálculo por transportadora.",
  },
  aceitoDivergencia: {
    title: "Aceito com divergência",
    short: "Diferença aceita na conferência da fatura (CT-e marcado OK)",
    color: "var(--everlog-status-info)",
    detailTitle: "Aceito com divergência",
    detailDescription: "A diferença foi aceita na fatura. Abra a fatura para ver quem aprovou e a justificativa.",
  },
  semDivergencia: {
    title: "Sem divergência",
    short: "CTe compatíveis com o valor simulado no período",
    color: "var(--everlog-status-success)",
    detailTitle: "Sem divergência",
    detailDescription: "Valide a saúde do cálculo e o volume processado sem intervenção.",
  },
};

// Status da API para cada card (PO1-3367: "3-aceito" = divergente aceito na fatura).
const STATUS_PARAM = {
  divergencia: "3-divergencia",
  aceitoDivergencia: "3-aceito",
  semDivergencia: "2",
};

const filterCardStyle = {
  ...autopilotInsetCardStyle,
  padding: "1rem 1rem 0.95rem",
  marginBottom: "1rem",
};

const sectionCardStyle = {
  ...autopilotInsetCardStyle,
  padding: "0.95rem 1rem",
};


const sectionTitleStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "0.96rem",
  fontWeight: 700,
  marginBottom: "0.25rem",
};

const sectionDescriptionStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.82rem",
  lineHeight: 1.5,
  marginBottom: "0.85rem",
};

const summaryTileBaseStyle = {
  width: "100%",
  borderRadius: "var(--everlog-card-radius-sm)",
  padding: "1rem",
  textAlign: "left",
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  background: AUTOPILOT_COLORS.insetSurface,
  cursor: "pointer",
  transition: "all 0.2s ease",
  minHeight: 168,
};

const metricCardStyle = {
  ...autopilotInsetCardStyle,
  padding: "0.85rem 0.95rem",
  background: "var(--everlog-card-surface-field)",
};

const metricLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.74rem",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "0.3rem",
};

const metricValueStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "1.08rem",
  fontWeight: 700,
  lineHeight: 1.2,
};

const quickButtonStyle = (active, color) => ({
  borderRadius: 999,
  border: `1px solid ${active ? color : AUTOPILOT_COLORS.border}`,
  background: active
    ? `color-mix(in srgb, ${color} 20%, var(--everlog-card-surface-field))`
    : "var(--everlog-card-surface-field)",
  color: active ? AUTOPILOT_COLORS.title : AUTOPILOT_COLORS.text,
  fontSize: "0.78rem",
  fontWeight: 600,
  padding: "0.38rem 0.75rem",
  cursor: "pointer",
  boxShadow: active ? "var(--everlog-card-interactive-shadow)" : "none",
});

const periodDateInputStyle = {
  background: "var(--everlog-card-surface-field)",
  borderColor: AUTOPILOT_COLORS.border,
  color: AUTOPILOT_COLORS.title,
  boxShadow: "none",
};

// Container de busca modelo auditoria-faturas (.conf-fatura-busca), portado inline
// com tokens AUTOPILOT_COLORS para manter a tela consistente. [PO1-2494]
const searchContainerStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(150px, 220px) minmax(0, 1fr) auto",
  alignItems: "center",
  gap: "0.85rem",
  padding: "0.9rem 1rem",
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  borderRadius: "12px",
  background: AUTOPILOT_COLORS.insetSurface,
  marginBottom: "1.75rem",
};

const searchIconStyle = {
  position: "absolute",
  left: "0.85rem",
  top: "50%",
  transform: "translateY(-50%)",
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.9rem",
  pointerEvents: "none",
};

const searchInputStyle = {
  width: "100%",
  minHeight: 46,
  paddingLeft: "2.4rem",
  borderRadius: 10,
  background: "var(--everlog-card-surface-field)",
  borderColor: AUTOPILOT_COLORS.border,
  color: AUTOPILOT_COLORS.title,
  boxShadow: "none",
};

const filtrosBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 18,
  height: 18,
  padding: "0 5px",
  borderRadius: 999,
  background: AUTOPILOT_COLORS.accent,
  color: AUTOPILOT_COLORS.onAccent,
  fontSize: "0.7rem",
  fontWeight: 700,
  marginLeft: "0.1rem",
};

const filtrosButtonStyle = (open) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  padding: "0.5rem 0.9rem",
  border: `1px solid ${open ? AUTOPILOT_COLORS.accent : AUTOPILOT_COLORS.border}`,
  borderRadius: 10,
  background: "transparent",
  color: open ? AUTOPILOT_COLORS.accent : AUTOPILOT_COLORS.text,
  fontSize: "0.85rem",
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
});

const tableHeadCellStyle = {
  color: AUTOPILOT_COLORS.muted,
  borderColor: AUTOPILOT_COLORS.border,
  fontWeight: 600,
  background: "var(--everlog-cte-table-head-bg)",
  position: "sticky",
  top: 0,
  zIndex: 1,
  whiteSpace: "nowrap",
};

const tableCellStyle = {
  color: AUTOPILOT_COLORS.text,
  borderColor: AUTOPILOT_COLORS.border,
  verticalAlign: "middle",
};

// PO1-3474: número, CNPJ, pedido e datas não quebram linha; a tabela rola
// na horizontal (Table responsive) quando não couber.
const detailTableCellStyle = {
  ...tableCellStyle,
  background: "var(--everlog-card-surface-field)",
  color: AUTOPILOT_COLORS.text,
  whiteSpace: "nowrap",
};

const nfLinkStyle = {
  background: "none",
  border: 0,
  padding: 0,
  color: AUTOPILOT_COLORS.accent,
  fontWeight: 700,
  textDecoration: "underline",
  cursor: "pointer",
};


const detailModalCloseButtonStyle = {
  border: 0,
  background: "transparent",
  color: "var(--everlog-card-text)",
  fontSize: "2rem",
  lineHeight: 1,
  padding: 0,
  cursor: "pointer",
  opacity: 0.95,
  textShadow: "none",
};

const AUDIT_STATUS_META = {
  ok: {
    label: "OK",
    color: "var(--everlog-status-success)",
    background: "var(--everlog-status-success-bg)",
  },
  divergente: {
    label: "Divergente",
    color: "var(--everlog-status-danger)",
    background: "var(--everlog-status-danger-bg)",
  },
  sem_dado: {
    label: "Sem dado",
    color: "var(--everlog-status-warning)",
    // warning nao tem token -bg no FLOW; usa superficie neutra (cor no texto).
    background: "var(--everlog-card-surface-field)",
  },
  informativo: {
    label: "Informativo",
    color: "var(--everlog-status-info)",
    background: "var(--everlog-card-surface-field)",
  },
};

const DETAIL_DIAGNOSIS_META = {
  critical: {
    badge: "Ação imediata",
    color: "var(--everlog-status-danger)",
    border: "var(--everlog-status-danger-border)",
    background: "var(--everlog-status-danger-bg)",
  },
  warning: {
    badge: "Atenção",
    color: "var(--everlog-status-warning)",
    border: "var(--everlog-status-warning)",
    background: "var(--everlog-card-surface-field)",
  },
  healthy: {
    badge: "Consistente",
    color: "var(--everlog-status-success)",
    border: "var(--everlog-status-success-border)",
    background: "var(--everlog-status-success-bg)",
  },
  neutral: {
    badge: "Em análise",
    color: "var(--everlog-status-info)",
    border: "var(--everlog-status-info)",
    background: "var(--everlog-card-surface-field)",
  },
};

const detailTabStyle = (active) => ({
  borderRadius: 999,
  border: `1px solid ${active ? AUTOPILOT_COLORS.accent : AUTOPILOT_COLORS.border}`,
  background: active ? "var(--everlog-card-surface-active)" : AUTOPILOT_COLORS.insetSurface,
  color: active ? "var(--everlog-card-title)" : AUTOPILOT_COLORS.muted,
  fontSize: "0.78rem",
  fontWeight: 600,
  padding: "0.42rem 0.82rem",
  cursor: "pointer",
  whiteSpace: "nowrap",
});

const detailDiagnosisCardStyle = (meta) => ({
  ...sectionCardStyle,
  padding: "1rem 1rem 1.05rem",
  marginBottom: "1rem",
  border: `1px solid ${meta.border}`,
  background: "var(--everlog-card-surface-elevated)",
  boxShadow: "var(--everlog-card-elevated-shadow)",
});

const detailDiagnosisBadgeStyle = (meta) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  borderRadius: 999,
  border: `1px solid ${meta.border}`,
  background: meta.background,
  color: meta.color,
  fontSize: "0.72rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  padding: "0.42rem 0.75rem",
  whiteSpace: "nowrap",
});

const detailDiagnosisTitleStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "1.08rem",
  fontWeight: 700,
  lineHeight: 1.2,
  margin: "0.8rem 0 0.35rem",
};

const detailDiagnosisDescriptionStyle = {
  color: AUTOPILOT_COLORS.text,
  fontSize: "0.9rem",
  lineHeight: 1.55,
  marginBottom: "0.9rem",
  width: "100%",
  maxWidth: "100%",
};

const overviewPanelStyle = {
  padding: "0.2rem",
  height: "100%",
  border: 0,
  background: "transparent",
  boxShadow: "none",
};

const overviewPanelTitleStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "1rem",
  fontWeight: 700,
  marginBottom: "0.24rem",
};

const overviewPanelDescriptionStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.8rem",
  lineHeight: 1.45,
  marginBottom: "0.85rem",
};

const overviewPanelEyebrowStyle = {
  color: "var(--everlog-status-info)",
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  marginBottom: "0.45rem",
};

const overviewFieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(148px, 1fr))",
  gap: "0.8rem 1rem",
};

const overviewFieldStyle = {
  minWidth: 0,
};

const overviewFieldLabelStyle = {
  color: "var(--everlog-card-muted)",
  fontSize: "0.67rem",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  marginBottom: "0.42rem",
};

const overviewFieldValueStyle = {
  color: "var(--everlog-card-title)",
  fontSize: "1.08rem",
  fontWeight: 700,
  lineHeight: 1.28,
};

const overviewFieldMutedValueStyle = {
  color: "var(--everlog-card-title)",
  fontSize: "1rem",
  fontWeight: 600,
  lineHeight: 1.42,
};

const overviewRouteCardStyle = {
  ...autopilotInsetCardStyle,
  padding: "0.95rem 1rem",
  background: "var(--everlog-card-surface-elevated)",
  borderColor: "var(--everlog-card-border)",
  boxShadow: "none",
  marginBottom: "0.85rem",
};

const overviewRouteLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  marginBottom: "0.4rem",
};

const overviewRouteValueStyle = {
  color: "var(--everlog-card-text)",
  fontSize: "1rem",
  fontWeight: 700,
  lineHeight: 1.35,
};

const overviewMiniMetricStyle = {
  ...autopilotInsetCardStyle,
  padding: "0.82rem 0.88rem",
  border: "1px solid var(--everlog-card-border)",
  background: "var(--everlog-card-surface-elevated)",
  boxShadow: "none",
};

const overviewMiniMetricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "0.75rem",
};

const overviewSectionCardStyle = {
  background: "var(--everlog-card-surface-elevated)",
  border: "1px solid var(--everlog-card-border)",
  boxShadow: "none",
};

const overviewKeyHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.65rem",
  marginBottom: "0.28rem",
};

const overviewCopyButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 28,
  minWidth: 28,
  padding: "0 0.55rem",
  borderRadius: 999,
  border: "1px solid var(--theme-accent-border)",
  background: "var(--everlog-card-surface-elevated)",
  color: "var(--everlog-card-text-soft)",
  fontSize: "0.75rem",
  fontWeight: 700,
  cursor: "pointer",
  whiteSpace: "nowrap",
  boxShadow: "var(--everlog-card-interactive-shadow)",
};

function getCteDetailDiagnosis({ summary, auditoria, auditoriaFinanceira, notasFiscais }) {
  if (!summary) {
    return {
      tone: "neutral",
      title: "Análise do documento",
      description: "Os detalhes do documento serão organizados aqui assim que a consulta for concluída.",
    };
  }

  const doc = refDocumento(summary.modelo);
  const divergente = Number(auditoria?.resumo?.divergente || 0);
  const semDado = Number(auditoria?.resumo?.semDado || 0);
  const totalNotas = Number(summary?.qtdNotasFiscais || 0) || (notasFiscais || []).length;
  const financialSummary = getFinancialDifferenceSummary({
    simulado: auditoriaFinanceira?.resumo?.totalSimulado ?? summary?.valorFreteSimulado,
    receber: auditoriaFinanceira?.resumo?.totalCte ?? summary?.valorFreteReceber,
    fallbackDifference: auditoriaFinanceira?.resumo?.diferenca ?? summary?.valorDivergencia,
    divergenteCte: summary?.staDivergencia,
  });
  const hasFinancialDiff = financialSummary.isMeaningful;
  if (!totalNotas) {
    return {
      tone: "critical",
      title: `${doc.rotulo} sem base suficiente para auditoria`,
      description: "Não há NFes vinculadas para sustentar a comparação do documento. O primeiro passo é validar o vínculo das notas e a origem da integração.",
    };
  }

  // PO1-3071: CT-e cancelado na SEFAZ e critico ANTES do financeiro. O fato fiscal
  // independe de o valor bater: o documento nao existe mais e nao deve ser pago. Sem
  // isto, um cancelado com valor conferindo caia no ramo "divergente > 0" da PO1-2502 e
  // o cabecalho dizia "Frete consistente" em verde. Mesma regra do componente
  // compartilhado components/cte/CteAuditDetailModal.js (esta tela tem copia propria).
  if (isCanceladoSefaz(summary)) {
    return {
      id: "cancelado_sefaz",
      tone: "critical",
      title: `${doc.rotulo} ${doc.cancelado} na SEFAZ`,
      description:
        `A SEFAZ registrou o cancelamento ${doc.deste}, mas ele não pôde ser removido do Hub porque está vinculado a fatura. Não aprove o pagamento: marque NOK na conferência da fatura para o cancelamento entrar no próximo ciclo.`,
    };
  }

  if (hasFinancialDiff) {
    const differenceLabel = formatCurrency(financialSummary.magnitude);
    const directionalDescription =
      financialSummary.direction === "simulado_above"
        ? `O simulado está ${differenceLabel} acima do frete a receber. Vale abrir a composição financeira antes de revisar os detalhes cadastrais.`
        : financialSummary.direction === "receber_above"
          ? `O frete a receber está ${differenceLabel} acima do simulado. Vale revisar cobrança, tabela aplicada e composição do cálculo antes de seguir.`
          : `O total confrontado entre ${doc.rotulo} e simulado aponta diferença de ${differenceLabel}. Vale abrir a composição financeira antes de revisar os detalhes cadastrais.`;

    return {
      tone: "critical",
      title:
        financialSummary.direction === "simulado_above"
          ? "Simulado acima do frete a receber"
          : financialSummary.direction === "receber_above"
            ? "Frete a receber acima do simulado"
            : `Diferença financeira identificada ${doc.neste}`,
      description: directionalDescription,
    };
  }

  if (divergente > 0) {
    // PO1-2502: aqui o financeiro JA esta consistente (passou pelo hasFinancialDiff).
    // Os checks divergentes sao nao-financeiros (peso, modal, valor da mercadoria, origem/
    // destino) e nao afetam o valor cobrado, entao nao alarmam (amarelo): o cabecalho fica
    // consistente (verde) com observacao suave, e os checks seguem visiveis (informativos).
    // PO1-3071: o check situacao_sefaz NAO entra aqui — e tratado como critico acima.
    return {
      tone: "healthy",
      title: "Frete consistente, com observações na composição",
      description: "O comparativo financeiro (frete a receber x simulado) está dentro do limite. Há divergências não-financeiras que não afetam o valor cobrado e seguem visíveis abaixo como informativas, úteis para checar a qualidade do dado (ex.: NF vinculada incorreta).",
    };
  }

  if (semDado > 0) {
    return {
      tone: "warning",
      title: "Faltam dados para fechar a conferência com segurança",
      description: "Parte da auditoria está sem base completa. O documento pode até parecer consistente, mas ainda depende de dados ausentes para uma validação confiável.",
    };
  }

  return {
    tone: "healthy",
    title: `${doc.rotulo} consistente com a composição atual`,
    description: "Os checks principais e a comparação financeira estão alinhados com a base disponível.",
  };
}

function buildLocationDisplay(city, uf) {
  const parts = [city, uf].filter(Boolean);
  return parts.length ? parts.join(" / ") : "-";
}

function getFinancialDifferenceSummary({ simulado, receber, fallbackDifference, divergenteCte }) {
  const numericSimulado = toNumberOrNull(simulado);
  const numericReceber = toNumberOrNull(receber);
  const fallback = toNumberOrNull(fallbackDifference);

  const directionalDifference =
    numericSimulado !== null && numericReceber !== null
      ? numericSimulado - numericReceber
      : null;

  const magnitude =
    directionalDifference !== null
      ? Math.abs(directionalDifference)
      : fallback !== null
        ? Math.abs(fallback)
        : null;

  // O CT-e ja carrega o veredito de divergencia (Sta_Divergencia), que respeita o
  // limite percentual da TMS. Quando ele diz que NAO e divergente, uma diferenca de
  // arredondamento de rateio (ex.: 2 centavos) nao deve disparar "simulado acima":
  // o autoritativo e o CT-e, nao o corte fixo de 1 centavo. Sem a flag (backend sem
  // o campo), cai no comportamento antigo por magnitude.
  const ctENaoDivergente = [false, 0, "0"].includes(divergenteCte);

  let direction = "unknown";
  if (ctENaoDivergente) {
    direction = "balanced";
  } else if (directionalDifference !== null) {
    if (directionalDifference > 0.01) direction = "simulado_above";
    else if (directionalDifference < -0.01) direction = "receber_above";
    else direction = "balanced";
  } else if (magnitude !== null && magnitude <= 0.01) {
    direction = "balanced";
  }

  return {
    simulado: numericSimulado,
    receber: numericReceber,
    directionalDifference,
    magnitude,
    displayValue: direction === "unknown" ? magnitude : directionalDifference,
    direction,
    isMeaningful: !ctENaoDivergente && magnitude !== null && magnitude > 0.01,
  };
}

async function copyTextToClipboard(value) {
  if (!value) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(String(value));
      return true;
    }
  } catch (error) {
    // fall back to legacy copy below
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = String(value);
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  } catch (error) {
    return false;
  }
}

const getTransportadoraKey = (entry) =>
  String(
    entry?.IdTransportadora ??
      entry?.idTransportadora ??
      entry?.transportadora_id ??
      entry?.transportadora ??
      "sem-transportadora"
  );

function mergeTransportadoras(entries) {
  const map = new Map();

  (entries || []).forEach((entry) => {
    const key = getTransportadoraKey(entry);
    const current = map.get(key) || {
      ...entry,
      quantidade: 0,
      totalFreteReceber: 0,
      totalFreteSimulado: 0,
    };

    current.quantidade += Number(entry.quantidade || 0);
    current.totalFreteReceber += Number(entry.totalFreteReceber || 0);
    current.totalFreteSimulado += Number(entry.totalFreteSimulado || 0);
    current.transportadora = current.transportadora || entry.transportadora || "-";
    current.IdTransportadora = current.IdTransportadora || entry.IdTransportadora || null;

    map.set(key, current);
  });

  return Array.from(map.values()).sort((left, right) => {
    const qtyDiff = Number(right.quantidade || 0) - Number(left.quantidade || 0);
    if (qtyDiff !== 0) return qtyDiff;
    return String(left.transportadora || "").localeCompare(String(right.transportadora || ""), "pt-BR");
  });
}

function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div
      style={{
        border: "1px solid var(--everlog-status-danger-border)",
        background: "var(--everlog-status-danger-bg)",
        color: "var(--everlog-status-danger)",
        borderRadius: 12,
        padding: "0.75rem 0.9rem",
        marginBottom: "1rem",
        fontSize: "0.84rem",
      }}
    >
      {message}
    </div>
  );
}

function StatusTile({ meta, total, active, onClick, loading, searchCount }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...summaryTileBaseStyle,
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        minHeight: "auto",
        padding: "0.85rem 1rem",
        // Borda dimmed via color-mix (saturacao suave, modelo auditoria-faturas); ativa = cor cheia + glow. [PO1-2495]
        borderColor: active ? meta.color : `color-mix(in srgb, ${meta.color} 38%, var(--everlog-card-border))`,
        boxShadow: active
          ? `0 0 0 2px color-mix(in srgb, ${meta.color} 22%, transparent), var(--everlog-card-interactive-shadow)`
          : "none",
        // Cards mais claros que o container (hero), destacando como na auditoria-faturas. [PO1-2495]
        background: active ? AUTOPILOT_COLORS.insetSurfaceActive : "var(--everlog-card-surface-field)",
        // Durante a busca global, apaga as etapas sem resultado para o usuário
        // enxergar de cara onde o CT-e está.
        opacity: searchCount === 0 ? 0.45 : 1,
      }}
    >
      {/* Layout horizontal: ponto centralizado + (label / numero) a esquerda, como na auditoria-faturas. [PO1-2495] */}
      <span
        style={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: meta.color,
          flexShrink: 0,
          boxShadow: `0 0 0 4px color-mix(in srgb, ${meta.color} 18%, transparent)`,
        }}
      />
      <span style={{ display: "flex", flexDirection: "column", gap: "0.2rem", alignItems: "flex-start", minWidth: 0 }}>
        <span
          style={{
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: `color-mix(in srgb, ${meta.color} 45%, var(--everlog-card-muted))`,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
          }}
        >
          {meta.title}
        </span>
        <span style={{ color: "var(--everlog-card-title)", fontSize: "1.5rem", fontWeight: 800, lineHeight: 1 }}>
          {loading ? <ClipLoader size={16} color={meta.color} /> : total}
        </span>
        {searchCount != null ? (
          <span
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              whiteSpace: "nowrap",
              color: searchCount > 0 ? meta.color : "var(--everlog-card-muted)",
            }}
          >
            {searchCount} na busca
          </span>
        ) : null}
      </span>
    </button>
  );
}

function SectionCard({ title, description, action, children, style }) {
  return (
    <div className="cte-audit-detail-section" style={{ ...sectionCardStyle, ...style }}>
      {(title || description || action) && (
        <div className="cte-audit-detail-section__head" style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", alignItems: "flex-start", marginBottom: children ? "0.8rem" : 0 }}>
          <div style={{ minWidth: 0 }}>
            {title ? <div className="cte-audit-detail-section__title" style={sectionTitleStyle}>{title}</div> : null}
            {description ? <div className="cte-audit-detail-section__description" style={sectionDescriptionStyle}>{description}</div> : null}
          </div>
          {action ? <div>{action}</div> : null}
        </div>
      )}
      {children}
    </div>
  );
}

function DetailMetric({ label, value, color }) {
  return (
    <div className="cte-audit-detail-metric" style={metricCardStyle}>
      <div style={metricLabelStyle}>{label}</div>
      <div style={{ ...metricValueStyle, color: color || AUTOPILOT_COLORS.title, fontSize: "0.96rem" }}>
        {value}
      </div>
    </div>
  );
}

function DetailField({ label, value, valueStyle }) {
  const displayValue =
    value === 0 || value === false ? value : value !== null && value !== undefined && value !== "" ? value : "-";

  return (
    <div className="cte-audit-detail-field" style={{ marginBottom: "0.9rem" }}>
      <div style={metricLabelStyle}>{label}</div>
      <div
        style={{
          color: AUTOPILOT_COLORS.text,
          fontSize: "0.96rem",
          lineHeight: 1.45,
          fontWeight: 600,
          minWidth: 0,
          ...valueStyle,
        }}
      >
        {displayValue}
      </div>
    </div>
  );
}

// Base do peso usada no cálculo (check "peso"): cubado quando a transportadora
// declarou peso base acima do bruto das NFes; bruto quando o cálculo usou o bruto.
const WEIGHT_BASIS_META = {
  cubado: {
    label: "cubado",
    color: "var(--everlog-status-info)",
    title: "Peso base do cálculo acima do peso bruto das NFes (cubagem declarada no CTe)",
  },
  bruto: {
    label: "bruto",
    color: "var(--everlog-card-muted)",
    title: "Cálculo feito sobre o peso bruto das NFes",
  },
};

function WeightBasisBadge({ basis }) {
  const meta = WEIGHT_BASIS_META[basis];
  if (!meta) return null;

  return (
    <span
      title={meta.title}
      style={{
        display: "inline-block",
        marginLeft: "0.4rem",
        borderRadius: 999,
        padding: "0.1rem 0.45rem",
        fontSize: "0.62rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        lineHeight: 1.4,
        color: meta.color,
        border: `1px solid ${meta.color}`,
        verticalAlign: "middle",
        whiteSpace: "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
}

function AuditWeightCell({ check }) {
  const bruto = check.nfesPesoBruto;
  const showBruto =
    bruto != null && check.nfesValor != null && Number(bruto) !== Number(check.nfesValor);

  return (
    <>
      {formatAuditValue(check.nfesValor, "peso")}
      <WeightBasisBadge basis={check.nfesBase} />
      {showBruto ? (
        <div style={{ fontSize: "0.72rem", color: "var(--everlog-card-muted)", marginTop: 2 }}>
          bruto NFes: {formatWeight(bruto)}
        </div>
      ) : null}
    </>
  );
}

// Situação OK com divergência acima da aceitável só existe por aprovação humana;
// o badge abre o modal com quem aprovou. Fica na coluna Divergência e, quando ela
// está oculta (card "Sem divergência"), ao lado do número do CTe.
function AprovadoManualmenteBadge({ onClick }) {
  return (
    <button
      type="button"
      title="Divergência acima da aceitável do cliente, aprovada manualmente — clique para ver quem aprovou"
      onClick={onClick}
      style={{
        display: "inline-block",
        borderRadius: 999,
        padding: "0.14rem 0.5rem",
        fontSize: "0.66rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        cursor: "pointer",
        color: "var(--everlog-status-success)",
        border: "1px solid var(--everlog-status-success)",
        background: "color-mix(in srgb, var(--everlog-status-success) 13%, transparent)",
      }}
    >
      Aprovado manualmente
    </button>
  );
}

function AuditStatusBadge({ status }) {
  const meta = AUDIT_STATUS_META[status] || AUDIT_STATUS_META.sem_dado;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 94,
        borderRadius: 999,
        padding: "0.35rem 0.7rem",
        fontSize: "0.74rem",
        fontWeight: 700,
        color: meta.color,
        background: meta.background,
        border: `1px solid ${meta.color}`,
        whiteSpace: "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
}

export default function AuditoriaCTe() {
  const { user } = useAuth();
  const { empresa, hasEmpresaSelection, transportadoraId } = useCompanies();
  const empresaId = getOptionValue(empresa);

  const [dataInicial, setDataInicial] = useState(() => {
    const hoje = new Date();
    const anterior = new Date();
    anterior.setDate(hoje.getDate() - 29);
    return toDateInputValue(anterior);
  });
  const [dataFinal, setDataFinal] = useState(() => toDateInputValue(new Date()));
  const [activeShortcut, setActiveShortcut] = useState("30 dias");
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [summary, setSummary] = useState({
    totalNaoCalculado1: 0,
    totalNaoCalculado3Simulado0: 0,
    totalDivergencia: 0,
    totalAceitoDivergencia: 0,
    totalSemDivergencia: 0,
  });
  const [transportadoras, setTransportadoras] = useState([]);
  const [details, setDetails] = useState([]);
  const [activeStatus, setActiveStatus] = useState(null);
  const [detailsSearch, setDetailsSearch] = useState("");
  const [detailsDivergenciaFilter, setDetailsDivergenciaFilter] = useState("todos");
  // Busca global: dataset com TODAS as etapas do período (details sem status),
  // para localizar um CT-e sem o usuário ter que clicar card por card.
  const [globalDetails, setGlobalDetails] = useState(null);
  const [loadingGlobalSearch, setLoadingGlobalSearch] = useState(false);
  const globalSearchCacheRef = useRef({ key: null, data: null });
  // Painel de filtros avançados colapsado por padrão; abre pelo botão "Filtros". [PO1-2494]
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [recalculoExec, setRecalculoExec] = useState(null); // { executionId, total, status }
  const [recalculoLoading, setRecalculoLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  // PO1-3400: tabela de preço aberta por cima do detalhe (null = fechada). Esta
  // tela tem cópia própria do detalhe do CT-e; o compartilhado já tinha o link.
  const [tabelaFreteAbertaId, setTabelaFreteAbertaId] = useState(null);
  // PO1-3478: NF e fatura abrem em modal, sem sair da Auditoria de CT-e.
  const [nfAbertaId, setNfAbertaId] = useState(null);
  const [faturaAberta, setFaturaAberta] = useState(null);
  // O modal da Auditoria de Fatura usa numFaturaExterna enquanto o detalhe carrega.
  const abrirFatura = useCallback((fatura) => setFaturaAberta({ ...fatura, numFaturaExterna: fatura.numero }), []);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [selectedCteDetail, setSelectedCteDetail] = useState(null);
  const [detailTab, setDetailTab] = useState("visao-geral");
  const [detailKeyCopyState, setDetailKeyCopyState] = useState("idle");
  const [selectedTransportadoraKey, setSelectedTransportadoraKey] = useState(null);
  // Aprovação em massa de divergência (PO1-1801): seleção só existe na fila
  // "Com divergência"; aprovar grava OOK no CT-e (PUT /ctes/validar) e o CT-e
  // sai da fila no reload.
  const [selectedCteIds, setSelectedCteIds] = useState(() => new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkFeedback, setBulkFeedback] = useState(null); // { type: 'success' | 'error', message }
  // Modal pequeno do badge "Aprovado manualmente": quem aprovou, data e hora.
  const [aprovacaoModal, setAprovacaoModal] = useState(null); // item do CT-e
  // PO1-3391: "Pedir análise à transportadora" com a mesma seleção da fila
  // "Com divergência". Guarda os ids no momento do clique (a seleção é
  // limpa ao enviar, e o modal mostra o resultado com os ids originais).
  const [pedirAnalise, setPedirAnalise] = useState(null); // { ids, itens } | null

  const totalNaoCalculado = useMemo(
    () => Number(summary.totalNaoCalculado1 || 0) + Number(summary.totalNaoCalculado3Simulado0 || 0),
    [summary.totalNaoCalculado1, summary.totalNaoCalculado3Simulado0]
  );

  const buildBaseParams = useCallback(
    () => ({
      embarcadorId: empresaId,
      dataInicial,
      dataFinal,
      ...(transportadoraId != null ? { transportadoraId } : {}),
    }),
    [empresaId, dataInicial, dataFinal, transportadoraId]
  );

  const loadDetailsData = useCallback(async (statusKey, transportadoraId = null) => {
    if (!empresaId || !statusKey) return;

    setLoadingDetails(true);
    setError(null);

    try {
      const baseParams = buildBaseParams();
      const detailsParams = transportadoraId
        ? { ...baseParams, transportadoraId }
        : baseParams;

      if (statusKey === "naoCalculado") {
        const respDetails = await api.get("/dashboard/auditoria/cte/details", {
          params: { ...detailsParams, status: "nao-calculado" },
        });
        setDetails(respDetails.data || []);
        return;
      }

      const statusParam = STATUS_PARAM[statusKey] || "2";
      const detailsResponse = await api.get("/dashboard/auditoria/cte/details", {
        params: { ...detailsParams, status: statusParam },
      });

      setDetails(detailsResponse.data || []);
    } catch (err) {
      setDetails([]);
      setError(err.response?.data?.error || err.message || "Erro ao carregar detalhes da fila.");
    } finally {
      setLoadingDetails(false);
    }
  }, [empresaId, buildBaseParams]);

  const loadSummary = useCallback(async () => {
    if (!empresaId) return;
    setLoadingSummary(true);
    setError(null);
    try {
      const response = await api.get("/dashboard/auditoria/cte/summary", {
        params: buildBaseParams(),
      });
      setSummary(response.data || {});
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Erro ao carregar resumo da auditoria.");
    } finally {
      setLoadingSummary(false);
    }
  }, [empresaId, buildBaseParams]);

  const loadStatusData = useCallback(async (statusKey) => {
    if (!empresaId || !statusKey) return;
    setError(null);

    try {
      const baseParams = buildBaseParams();

      if (statusKey === "naoCalculado") {
        const respTransp = await api.get("/dashboard/auditoria/cte/transportadora", {
          params: { ...baseParams, status: "nao-calculado" },
        });
        setTransportadoras(mergeTransportadoras(respTransp.data || []));
        await loadDetailsData(statusKey, null);
        return;
      }

      const statusParam = STATUS_PARAM[statusKey] || "2";
      const [transportadorasResponse] = await Promise.all([
        api.get("/dashboard/auditoria/cte/transportadora", {
          params: { ...baseParams, status: statusParam },
        }),
      ]);

      setTransportadoras(mergeTransportadoras(transportadorasResponse.data || []));
      await loadDetailsData(statusKey, null);
    } catch (err) {
      setTransportadoras([]);
      setDetails([]);
      setError(err.response?.data?.error || err.message || "Erro ao carregar detalhes da fila.");
    }
  }, [empresaId, buildBaseParams, loadDetailsData]);

  useEffect(() => {
    if (!empresaId) return;
    loadSummary();
  }, [loadSummary, empresaId]);

  useEffect(() => {
    if (!empresaId || !activeStatus) return;
    loadStatusData(activeStatus);
  }, [activeStatus, empresaId, loadStatusData]);

  const searchQuery = detailsSearch.trim().toLowerCase();
  const isSearching = searchQuery.length > 0;
  // No card "Sem divergência" a coluna Divergência confunde: mostra centavos de
  // arredondamento em vermelho/verde num recorte que, por definição, não diverge.
  // Some nas duas tabelas e no filtro Com/Sem; a busca global (mistura etapas) mantém.
  const hideDivergencia = !isSearching && activeStatus === "semDivergencia";

  // Carrega o dataset global (todas as etapas) quando a busca é usada; um
  // fetch por recorte (empresa/período/transportadora), reaproveitado via
  // cache enquanto o usuário digita.
  useEffect(() => {
    if (!isSearching || !empresaId) return undefined;

    const key = JSON.stringify(buildBaseParams());
    if (globalSearchCacheRef.current.key === key && globalSearchCacheRef.current.data) {
      setGlobalDetails(globalSearchCacheRef.current.data);
      return undefined;
    }

    setGlobalDetails(null);
    let cancelled = false;
    const timeout = setTimeout(async () => {
      setLoadingGlobalSearch(true);
      try {
        // Sem o param status o endpoint devolve os CT-es de todas as etapas.
        const response = await api.get("/dashboard/auditoria/cte/details", {
          params: buildBaseParams(),
        });
        if (cancelled) return;
        const data = response.data || [];
        globalSearchCacheRef.current = { key, data };
        setGlobalDetails(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || "Erro ao buscar CT-e em todas as etapas.");
        }
      } finally {
        if (!cancelled) setLoadingGlobalSearch(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [isSearching, empresaId, buildBaseParams]);

  useEffect(() => {
    if (hasEmpresaSelection) return;
    setSummary({
      totalNaoCalculado1: 0,
      totalNaoCalculado3Simulado0: 0,
      totalDivergencia: 0,
      totalSemDivergencia: 0,
    });
    setTransportadoras([]);
    setDetails([]);
    setActiveStatus(null);
    setDetailsSearch("");
    setDetailsDivergenciaFilter("todos");
    setGlobalDetails(null);
    setSelectedTransportadoraKey(null);
    setError(null);
    setRecalculoExec(null);
  }, [hasEmpresaSelection]);

  useEffect(() => {
    setTransportadoras([]);
    setDetails([]);
    setDetailsSearch("");
    setDetailsDivergenciaFilter("todos");
    setGlobalDetails(null);
    setSelectedTransportadoraKey(null);
    setError(null);
  }, [transportadoraId]);

  useEffect(() => {
    if (detailKeyCopyState === "idle") return undefined;

    const timeout = setTimeout(() => {
      setDetailKeyCopyState("idle");
    }, 1600);

    return () => clearTimeout(timeout);
  }, [detailKeyCopyState]);

  const activeMeta = activeStatus ? STATUS_META[activeStatus] : null;
  const selectedTransportadora = useMemo(
    () => transportadoras.find((item) => getTransportadoraKey(item) === selectedTransportadoraKey) || null,
    [selectedTransportadoraKey, transportadoras]
  );

  const detailsByTransportadora = useMemo(() => {
    if (!selectedTransportadoraKey) return details;
    return details.filter((item) => getTransportadoraKey(item) === selectedTransportadoraKey);
  }, [details, selectedTransportadoraKey]);

  const filteredDetails = useMemo(() => {
    // Busca global: procura em todas as etapas e transportadoras do período,
    // ignorando o card selecionado, para localizar o CT-e sem o usuário ter
    // que adivinhar em qual fila ele está.
    if (isSearching) {
      const dataset = globalDetails || detailsByTransportadora;
      return dataset.filter((item) => {
        const searchText = [
          item.Id,
          item.numero,
          item.chaveAcesso,
          item.transportadora,
          item.situacao,
          formatDateLabel(item.dataEmissao),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchText.includes(searchQuery);
      });
    }

    if (detailsDivergenciaFilter === "todos" || hideDivergencia) return detailsByTransportadora;

    return detailsByTransportadora.filter((item) => {
      const div = calcDivergencia(item.valorFreteReceber, item.valorFreteSimulado);
      // div===null means simulado=0; treat as divergência when receber>0 (uncalculated but billed)
      const hasDivergencia =
        div !== null ? div !== 0 : Number(item.valorFreteReceber || 0) > 0;
      return detailsDivergenciaFilter === "com" ? hasDivergencia : !hasDivergencia;
    });
  }, [detailsByTransportadora, globalDetails, isSearching, searchQuery, detailsDivergenciaFilter, hideDivergencia]);

  // Aprovação em massa (PO1-1801): habilitada apenas na fila de divergência
  // (fora da busca global, que mistura etapas).
  const bulkSelectable = !isSearching && activeStatus === "divergencia";
  const selectableIds = useMemo(
    () => (bulkSelectable ? filteredDetails.map((item) => item.Id) : []),
    [bulkSelectable, filteredDetails]
  );
  const allVisibleSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedCteIds.has(id));

  const toggleCteSelection = useCallback((id) => {
    setSelectedCteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedCteIds(allVisibleSelected ? new Set() : new Set(selectableIds));
  }, [allVisibleSelected, selectableIds]);

  // Troca de fila/recorte invalida a seleção (os ids visíveis mudam).
  useEffect(() => {
    setSelectedCteIds(new Set());
    setBulkFeedback(null);
  }, [details, activeStatus, selectedTransportadoraKey]);

  const handleAprovarSelecionados = useCallback(async () => {
    const ids = Array.from(selectedCteIds);
    if (!ids.length || !empresaId || bulkSaving) return;
    const rotuloSelecionados = rotuloDocumentos(filteredDetails.filter((item) => selectedCteIds.has(item.Id)));
    setBulkSaving(true);
    setBulkFeedback(null);
    try {
      const { data } = await api.put("/ctes/validar", {
        embarcadorId: empresaId,
        situacao: "OOK",
        ids,
      });
      const atualizados = data?.atualizados ?? ids.length;
      const ignorados = data?.ignorados || 0;
      setBulkFeedback({
        type: "success",
        message: `${atualizados} ${rotuloSelecionados} aprovado(s)${
          ignorados ? ` — ${ignorados} ignorado(s) (fora da fila/escopo)` : ""
        }.`,
      });
      setSelectedCteIds(new Set());
      // Aprovado sai da fila de divergência: recarrega lista e contadores.
      await Promise.all([loadSummary(), loadDetailsData(activeStatus)]);
    } catch (err) {
      const apiError = err.response?.data?.error;
      setBulkFeedback({
        type: "error",
        message:
          (typeof apiError === "string" ? apiError : null) ||
          err.message ||
          `Erro ao aprovar ${rotuloSelecionados}.`,
      });
    } finally {
      setBulkSaving(false);
    }
  }, [selectedCteIds, filteredDetails, empresaId, bulkSaving, loadSummary, loadDetailsData, activeStatus]);

  const abrirPedirAnalise = useCallback(() => {
    const ids = Array.from(selectedCteIds);
    if (!ids.length || ids.length > MAX_CTES_POR_ENVIO || !empresaId) return;
    setBulkFeedback(null);
    setPedirAnalise({ ids, itens: filteredDetails.filter((item) => selectedCteIds.has(item.Id)) });
  }, [selectedCteIds, filteredDetails, empresaId]);

  // Enviado: limpa a seleção e dá o resumo na faixa. Não recarrega a lista —
  // o status do CT-e não muda (o pedido vive só no e-mail e no log).
  const handlePedidoEnviado = useCallback((data) => {
    const enviados = (data?.resultados || []).filter((r) => r.enviado).length;
    const falhas = (data?.resultados || []).length - enviados;
    setSelectedCteIds(new Set());
    setBulkFeedback({
      type: falhas ? "error" : "success",
      message: `${enviados} e-mail(s) de pedido de análise enviado(s)${falhas ? ` — ${falhas} transportadora(s) sem envio` : ""}${
        data?.ignorados ? ` — ${data.ignorados} ignorado(s)` : ""
      }.`,
    });
  }, []);

  // Resultados da busca por etapa, exibidos nos cards de status para o
  // usuário ver de cara onde o CT-e está.
  const searchMatchCounts = useMemo(() => {
    if (!isSearching) return null;
    const counts = { naoCalculado: 0, divergencia: 0, aceitoDivergencia: 0, semDivergencia: 0 };
    filteredDetails.forEach((item) => {
      const bucket = getStatusBucket(item);
      if (bucket && counts[bucket] !== undefined) counts[bucket] += 1;
    });
    return counts;
  }, [isSearching, filteredDetails]);

  const detailMetrics = useMemo(() => {
    if (isSearching) {
      const carriers = new Set(
        filteredDetails
          .map((item) => item.IdTransportadora || item.transportadora)
          .filter(Boolean)
      );
      return {
        transportadoras: carriers.size,
        documentos: filteredDetails.length,
        freteReceber: filteredDetails.reduce((acc, item) => acc + Number(item.valorFreteReceber || 0), 0),
        freteSimulado: filteredDetails.reduce((acc, item) => acc + Number(item.valorFreteSimulado || 0), 0),
      };
    }

    if (selectedTransportadora) {
      return {
        transportadoras: detailsByTransportadora.length > 0 ? 1 : 0,
        documentos: detailsByTransportadora.length,
        freteReceber: Number(selectedTransportadora.totalFreteReceber || 0),
        freteSimulado: Number(selectedTransportadora.totalFreteSimulado || 0),
      };
    }

    const totalFreteReceber = transportadoras.reduce((acc, item) => acc + Number(item.totalFreteReceber || 0), 0);
    const totalFreteSimulado = transportadoras.reduce((acc, item) => acc + Number(item.totalFreteSimulado || 0), 0);

    return {
      transportadoras: transportadoras.length,
      documentos: details.length,
      freteReceber: totalFreteReceber,
      freteSimulado: totalFreteSimulado,
    };
  }, [details.length, detailsByTransportadora.length, filteredDetails, isSearching, selectedTransportadora, transportadoras]);

  // Contagem de filtros avançados ativos (badge do botão Filtros). [PO1-2494]
  const filtrosAtivosCount =
    (activeShortcut !== "30 dias" ? 1 : 0) +
    (detailsDivergenciaFilter !== "todos" ? 1 : 0) +
    (activeStatus ? 1 : 0) +
    (selectedTransportadoraKey ? 1 : 0);

const handleFilter = () => {
    loadSummary();
    if (activeStatus) {
      loadStatusData(activeStatus);
    }
  };

  const handleShortcutClick = (shortcut) => {
    const dates = shortcut.getDates();
    setDataInicial(dates.de);
    setDataFinal(dates.ate);
    setActiveShortcut(shortcut.label);
  };

  const handleManualDateChange = (setter) => (event) => {
    setActiveShortcut(null);
    setter(event.target.value);
  };

  const handleCalcularPendentesInline = useCallback(async () => {
    const chaves = filteredDetails
      .map((d) => d.chaveAcesso)
      .filter(Boolean);

    if (!chaves.length) return;

    setRecalculoLoading(true);
    setRecalculoExec(null);
    try {
      const response = await api.post("/recalculo/cte/por-chaves", {
        embarcadorId: empresaId,
        chaves,
      });
      setRecalculoExec(response.data);
    } catch (err) {
      setRecalculoExec({ erro: err.response?.data?.error || "Erro ao enfileirar recálculo." });
    } finally {
      setRecalculoLoading(false);
    }
  }, [filteredDetails, empresaId]);

  const handleCardClick = (statusKey) => {
    // Não limpa a busca: ela é global (todas as etapas) e independe do card.
    setDetailsDivergenciaFilter("todos");
    setSelectedTransportadoraKey(null);

    if (activeStatus === statusKey) {
      setActiveStatus(null);
      setTransportadoras([]);
      setDetails([]);
      return;
    }

    setActiveStatus(statusKey);
    setTransportadoras([]);
    setDetails([]);
  };

  const handleTransportadoraClick = (transportadora) => {
    const nextKey = getTransportadoraKey(transportadora);
    const isSameTransportadora = selectedTransportadoraKey === nextKey;
    const nextTransportadoraId = !isSameTransportadora ? transportadora?.IdTransportadora || null : null;

    setSelectedTransportadoraKey(isSameTransportadora ? null : nextKey);
    loadDetailsData(activeStatus, nextTransportadoraId);
  };

  const exportDetailsToCsv = () => {
    if (!filteredDetails || filteredDetails.length === 0) return;

    const header = [
      "ID",
      "Tipo",
      "Serie",
      "Transportadora",
      "DataEmissao",
      "ValorFreteReceber",
      "ValorFreteSimulado",
      "Divergencia",
      "Chave",
    ];

    const rows = filteredDetails.map((d) => {
      const delta = calcDivergencia(d.valorFreteReceber, d.valorFreteSimulado);
      return [
        d.Id,
        rotuloDocumento(d.modelo),
        d.serie || "",
        d.transportadora || "",
        d.dataEmissao ? formatDateLabel(d.dataEmissao) : "",
        d.valorFreteReceber != null ? Number(d.valorFreteReceber).toFixed(2) : "",
        d.valorFreteSimulado != null ? Number(d.valorFreteSimulado).toFixed(2) : "",
        delta != null ? delta.toFixed(2) : "",
        d.chaveAcesso || "",
      ];
    });

    const csvContent = [header, ...rows]
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const statusLabel = activeStatus ? STATUS_META[activeStatus].title.toLowerCase().replace(/\s+/g, "_") : "auditoria";
    link.href = url;
    link.setAttribute("download", `auditoria_cte_${statusLabel}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleOpenCteDetail = async (cte) => {
    if (!cte || !cte.Id) return;

    setDetailModalOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    setSelectedCteDetail(null);
    setDetailTab("visao-geral");
    setDetailKeyCopyState("idle");

    try {
      const response = await api.get(`/dashboard/auditoria/cte/details/${cte.Id}`, {
        params: { embarcadorId: empresaId },
      });
      setSelectedCteDetail(response.data || null);
    } catch (err) {
      setDetailError(err.response?.data?.error || err.message || `Erro ao carregar detalhe ${refDocumento(cte.modelo).do}.`);
    } finally {
      setDetailLoading(false);
    }
  };

  // De/para taxa <-> componente criado direto da auditoria (arrastar e soltar na
  // tabela "Componentes do CT-e x simulado"). É o mesmo vínculo da tela Taxas de
  // Frete (FreteTaxa_DP_ComponentePrestacaoServico), global por taxa. Depois do
  // POST recarrega só o detalhe, sem mexer na aba aberta.
  const handleLinkComponent = async ({ idFreteTaxa, idComponentePrestacaoServico }) => {
    await api.post(`/cadastros/taxas-frete/${idFreteTaxa}/componentes`, { idComponentePrestacaoServico });
    const cteId = selectedCteDetail?.summary?.id;
    if (!cteId) return;
    const response = await api.get(`/dashboard/auditoria/cte/details/${cteId}`, {
      params: { embarcadorId: empresaId },
    });
    setSelectedCteDetail(response.data || null);
  };

  // PO1-3197: recarrega o detalhe depois do cálculo feito pelo botão do cabeçalho,
  // sem o carregando (a mensagem do resultado continua visível).
  const handleRecalculatedCte = async () => {
    const cteId = selectedCteDetail?.summary?.id;
    if (!cteId) return;
    try {
      const response = await api.get(`/dashboard/auditoria/cte/details/${cteId}`, {
        params: { embarcadorId: empresaId },
      });
      setSelectedCteDetail(response.data || null);
    } catch (err) {
      setDetailError(err.response?.data?.error || err.message || `Erro ao recarregar detalhe ${refDocumento(selectedCteDetail?.summary?.modelo).do}.`);
    }
  };

  const handleCloseDetail = () => {
    setDetailModalOpen(false);
    setTabelaFreteAbertaId(null);
    setDetailLoading(false);
    setDetailError(null);
    setSelectedCteDetail(null);
    setDetailTab("visao-geral");
    setDetailKeyCopyState("idle");
  };

  const handleCopyDetailAccessKey = useCallback(async () => {
    const value = selectedCteDetail?.summary?.chaveAcesso;
    if (!value) return;

    const copied = await copyTextToClipboard(value);
    setDetailKeyCopyState(copied ? "copied" : "error");
  }, [selectedCteDetail]);

  if (!hasEmpresaSelection) {
    return (
      <div className="content">
        <SpecificEmpresaRequiredNotice />
      </div>
    );
  }

  const detailSummary = selectedCteDetail?.summary || null;
  // PO1-3367: fatura do CT-e (situação, decisão e histórico da conferência).
  const detailFatura = selectedCteDetail?.fatura || null;
  const detailDoc = refDocumento(detailSummary && detailSummary.modelo);
  const detailNfs = selectedCteDetail?.notasFiscais || [];
  const detailPartes = selectedCteDetail?.partes || {};
  const detailComponentes = selectedCteDetail?.componentes || [];
  const detailImpostos = selectedCteDetail?.impostos || [];
  const detailCarga = selectedCteDetail?.carga || [];
  const detailAuditoria = selectedCteDetail?.auditoria || { resumo: null, checks: [] };
  const detailAuditoriaFinanceira = selectedCteDetail?.auditoriaFinanceira || {
    resumo: null,
    tabelaPrecoSimulado: null,
    grupos: [],
    componentesComparativo: [],
    frete: { itens: [], cteSelecionado: null, simuladoSelecionado: null },
    taxas: { itens: [] },
    tributos: { itens: [] },
  };
  const detailFinancialGroups = (detailAuditoriaFinanceira?.grupos || []).filter(
    (check) => check.id !== "tributos" || check.tributoIncideNoTotal !== false
  );
  const detailFinancialSummary = getFinancialDifferenceSummary({
    simulado: detailAuditoriaFinanceira?.resumo?.totalSimulado ?? detailSummary?.valorFreteSimulado,
    receber: detailAuditoriaFinanceira?.resumo?.totalCte ?? detailSummary?.valorFreteReceber,
    fallbackDifference: detailAuditoriaFinanceira?.resumo?.diferenca ?? detailSummary?.valorDivergencia,
    divergenteCte: detailSummary?.staDivergencia,
  });
  const detailFinancialDifferenceColor =
    detailFinancialSummary.magnitude === null
      ? AUTOPILOT_COLORS.text
      : detailFinancialSummary.isMeaningful
        ? "var(--everlog-status-danger)"
        : "var(--everlog-status-success)";
  const detailOriginLabel = buildLocationDisplay(detailSummary?.cidadeOrigem, detailSummary?.ufOrigem);
  const detailDestinationLabel = buildLocationDisplay(detailSummary?.cidadeDestino, detailSummary?.ufDestino);
  const detailAdicionais = selectedCteDetail?.adicionais || null;
  const detailDiagnosis = getCteDetailDiagnosis({
    summary: detailSummary,
    auditoria: detailAuditoria,
    auditoriaFinanceira: detailAuditoriaFinanceira,
    notasFiscais: detailNfs,
  });
  const detailDiagnosisMeta =
    DETAIL_DIAGNOSIS_META[detailDiagnosis.tone] || DETAIL_DIAGNOSIS_META.neutral;
  const detailTabs = [
    { id: "visao-geral", label: "Visão geral" },
    { id: "auditoria", label: "Auditoria" },
    { id: "notas", label: "NFes" },
    { id: "fatura", label: "Fatura" },
  ];
  const detailModalClose = (
    <button
      type="button"
      aria-label="Fechar"
      onClick={handleCloseDetail}
      className="cte-audit-detail-close"
      style={detailModalCloseButtonStyle}
    >
      ×
    </button>
  );

  return (
    <div className="content">
      {/* Hero padrão do Hub (PO1-2724): mesmo visual de antes, agora via PageHero. */}
      <PageHero
        eyebrow="Operação"
        title="Auditoria de CTe"
        description="Acompanhe backlog, pendências e divergências de CTe por transportadora."
        actions={
          <>
            <Button
              className="btn-sm"
              style={autopilotSecondaryButtonStyle}
              onClick={handleFilter}
              disabled={loadingSummary || loadingDetails}
            >
              {loadingSummary || loadingDetails ? <ClipLoader size={12} color="var(--everlog-on-accent-text)" /> : "↺ Atualizar"}
            </Button>
            <Button color="success" className="btn-sm" onClick={exportDetailsToCsv} disabled={!filteredDetails.length}>
              Exportar CSV
            </Button>
          </>
        }
      />

      <Card
        className="card-everlog-shell"
        style={{
          ...autopilotCardStyle,
          marginTop: "1.25rem",
          background: "var(--everlog-card-hero-bg)",
        }}
      >
        <CardBody style={autopilotBodyStyle}>
          {/* Container de busca (modelo auditoria-faturas): campo principal Nr.CTE/Chave + botao Filtros. [PO1-2494] */}
          <div style={searchContainerStyle}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.9rem", fontWeight: 700, lineHeight: 1.2 }}>
                Buscar CT-e
              </div>
              <div style={{ color: AUTOPILOT_COLORS.textSoft, fontSize: "0.74rem", lineHeight: 1.35, marginTop: "0.15rem" }}>
                Nr. do CT-e ou chave de acesso
              </div>
            </div>
            <div style={{ position: "relative", minWidth: 0 }}>
              <i className="tim-icons icon-zoom-split" style={searchIconStyle} />
              <Input
                type="search"
                value={detailsSearch}
                onChange={(event) => setDetailsSearch(event.target.value)}
                placeholder="Digite o Nr. do CT-e ou a chave de acesso (44 dígitos)"
                style={searchInputStyle}
              />
            </div>
            <button
              type="button"
              onClick={() => setMostrarFiltros((v) => !v)}
              aria-expanded={mostrarFiltros}
              style={filtrosButtonStyle(mostrarFiltros)}
            >
              <i className="tim-icons icon-settings-gear-63" />
              <span>Filtros</span>
              {filtrosAtivosCount > 0 ? (
                <span style={filtrosBadgeStyle}>{filtrosAtivosCount}</span>
              ) : null}
            </button>
          </div>

          {/* Painel de filtros avancados (collapse pelo botao Filtros). [PO1-2494] */}
          <div style={{ display: mostrarFiltros ? "block" : "none", marginTop: "0.85rem" }}>
            <SectionCard title="Filtros" description="Recorte por período, atalhos rápidos e divergência." style={filterCardStyle}>
              <Row>
                <Col md={4}>
                  <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.76rem", marginBottom: "0.35rem" }}>Data inicial</div>
                  <Input
                    type="date"
                    value={dataInicial}
                    onChange={handleManualDateChange(setDataInicial)}
                    style={periodDateInputStyle}
                  />
                </Col>
                <Col md={4}>
                  <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.76rem", marginBottom: "0.35rem" }}>Data final</div>
                  <Input
                    type="date"
                    value={dataFinal}
                    onChange={handleManualDateChange(setDataFinal)}
                    style={periodDateInputStyle}
                  />
                </Col>
                <Col md={4}>
                  <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.76rem", marginBottom: "0.35rem" }}>Atalhos</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.45rem" }}>
                    {DATE_SHORTCUTS.map((shortcut) => (
                      <button
                        key={shortcut.label}
                        type="button"
                        style={quickButtonStyle(activeShortcut === shortcut.label, AUTOPILOT_COLORS.accent)}
                        onClick={() => handleShortcutClick(shortcut)}
                      >
                        {shortcut.label}
                      </button>
                    ))}
                  </div>
                </Col>
              </Row>
              {hideDivergencia ? null : (
              <div style={{ marginTop: "0.9rem" }}>
                <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.76rem", marginBottom: "0.35rem" }}>Divergência</div>
                <div className="btn-group btn-group-sm" role="group">
                  <Button
                    color={detailsDivergenciaFilter === "todos" ? "secondary" : "outline-secondary"}
                    size="sm"
                    onClick={() => setDetailsDivergenciaFilter("todos")}
                  >
                    Todos
                  </Button>
                  <Button
                    color={detailsDivergenciaFilter === "com" ? "danger" : "outline-secondary"}
                    size="sm"
                    onClick={() => setDetailsDivergenciaFilter("com")}
                  >
                    Com divergência
                  </Button>
                  <Button
                    color={detailsDivergenciaFilter === "sem" ? "success" : "outline-secondary"}
                    size="sm"
                    onClick={() => setDetailsDivergenciaFilter("sem")}
                  >
                    Sem divergência
                  </Button>
                </div>
              </div>
              )}
            </SectionCard>
          </div>

          <ErrorBanner message={error} />

          {/* Cards de status agrupados num container "Status" (modelo auditoria-faturas). [PO1-2495] */}
          <SectionCard
            title="Status"
            description="Selecione uma etapa para filtrar a fila."
            style={{ marginBottom: "1rem" }}
          >
            <Row>
            <Col lg={3} md={6} style={{ marginBottom: 12 }}>
              <StatusTile
                meta={STATUS_META.naoCalculado}
                total={totalNaoCalculado}
                active={activeStatus === "naoCalculado"}
                loading={loadingSummary}
                onClick={() => handleCardClick("naoCalculado")}
                searchCount={searchMatchCounts ? searchMatchCounts.naoCalculado : null}
              />
            </Col>
            <Col lg={3} md={6} style={{ marginBottom: 12 }}>
              <StatusTile
                meta={STATUS_META.divergencia}
                total={summary.totalDivergencia || 0}
                active={activeStatus === "divergencia"}
                loading={loadingSummary}
                onClick={() => handleCardClick("divergencia")}
                searchCount={searchMatchCounts ? searchMatchCounts.divergencia : null}
              />
            </Col>
            <Col lg={3} md={6} style={{ marginBottom: 12 }}>
              <StatusTile
                meta={STATUS_META.aceitoDivergencia}
                total={summary.totalAceitoDivergencia || 0}
                active={activeStatus === "aceitoDivergencia"}
                loading={loadingSummary}
                onClick={() => handleCardClick("aceitoDivergencia")}
                searchCount={searchMatchCounts ? searchMatchCounts.aceitoDivergencia : null}
              />
            </Col>
            <Col lg={3} md={6} style={{ marginBottom: 12 }}>
              <StatusTile
                meta={STATUS_META.semDivergencia}
                total={summary.totalSemDivergencia || 0}
                active={activeStatus === "semDivergencia"}
                loading={loadingSummary}
                onClick={() => handleCardClick("semDivergencia")}
                searchCount={searchMatchCounts ? searchMatchCounts.semDivergencia : null}
              />
            </Col>
            </Row>

            {activeStatus && activeMeta ? (
              <div
                style={{
                  marginTop: "0.85rem",
                  padding: "0.75rem 0.85rem",
                  borderRadius: 10,
                  border: `1px solid color-mix(in srgb, ${activeMeta.color} 42%, var(--everlog-card-border-faint))`,
                  background: "var(--everlog-card-surface-field)",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.45rem",
                    color: `color-mix(in srgb, ${activeMeta.color} 48%, var(--everlog-card-muted))`,
                    fontSize: "0.68rem",
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: activeMeta.color, flexShrink: 0 }} />
                  Status selecionado
                </div>
                <div style={{ color: "var(--everlog-card-title)", fontSize: "0.92rem", fontWeight: 700, marginTop: "0.35rem" }}>
                  {activeMeta.detailTitle}
                </div>
                {activeMeta.short ? (
                  <div style={{ color: "var(--everlog-card-text-soft)", fontSize: "0.78rem", marginTop: "0.2rem", lineHeight: 1.4 }}>
                    {activeMeta.short}
                  </div>
                ) : null}
              </div>
            ) : null}
          </SectionCard>

          {!activeStatus && !isSearching ? null : (
            <div style={{ marginTop: "0.5rem" }}>
              <SectionCard
                style={{ marginBottom: "1rem" }}
                action={
                  (!isSearching && (activeStatus === "naoCalculado" || activeStatus === "divergencia" || activeStatus === "semDivergencia")) ? (
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                      {recalculoExec && !recalculoExec.erro && (
                        <Badge
                          color={recalculoExec.status === "queued" || recalculoExec.status === "running" ? "warning" : "success"}
                          style={{ fontSize: "0.72rem", padding: "0.35em 0.65em" }}
                        >
                          {recalculoExec.status === "queued" || recalculoExec.status === "running"
                            ? `Em processamento — ${recalculoExec.total} CTe(s)`
                            : `Enfileirado — ${recalculoExec.total} CTe(s)`}
                        </Badge>
                      )}
                      {recalculoExec && !recalculoExec.erro && recalculoExec.ignoradas && recalculoExec.ignoradas.length ? (
                        <Badge
                          color="secondary"
                          title={recalculoExec.ignoradas.map((i) => i.motivo).join("\n")}
                          style={{ fontSize: "0.72rem", padding: "0.35em 0.65em", cursor: "help" }}
                        >
                          {`${recalculoExec.ignoradas.length} CTe(s) fora: fatura encerrada`}
                        </Badge>
                      ) : null}
                      {recalculoExec && recalculoExec.erro && (
                        <Badge color="danger" style={{ fontSize: "0.72rem", padding: "0.35em 0.65em" }}>
                          {recalculoExec.erro}
                        </Badge>
                      )}
                      <button
                        type="button"
                        onClick={handleCalcularPendentesInline}
                        disabled={recalculoLoading || !filteredDetails.length}
                        style={{
                          ...autopilotSecondaryButtonStyle,
                          whiteSpace: "nowrap",
                          fontSize: "0.78rem",
                          opacity: recalculoLoading || !details.length ? 0.5 : 1,
                        }}
                      >
                        {recalculoLoading
                          ? <ClipLoader size={12} color="currentColor" style={{ marginRight: 5 }} />
                          : <i className="tim-icons icon-refresh-01" style={{ marginRight: 5 }} />}
                        Calcular pendentes
                      </button>
                    </div>
                  ) : null
                }
              >
                <Row>
                  <Col md={3} style={{ marginBottom: 12 }}>
                    <div style={metricCardStyle}>
                      <div style={metricLabelStyle}>Transportadoras</div>
                      <div style={metricValueStyle}>{detailMetrics.transportadoras}</div>
                    </div>
                  </Col>
                  <Col md={3} style={{ marginBottom: 12 }}>
                    <div style={metricCardStyle}>
                      <div style={metricLabelStyle}>{isSearching ? "CTe encontrados" : "CTe carregados"}</div>
                      <div style={metricValueStyle}>{detailMetrics.documentos}</div>
                    </div>
                  </Col>
                  <Col md={3} style={{ marginBottom: 12 }}>
                    <div style={metricCardStyle}>
                      <div style={metricLabelStyle}>Frete a receber</div>
                      <div style={{ ...metricValueStyle, fontSize: "0.96rem" }}>{formatCurrency(detailMetrics.freteReceber)}</div>
                    </div>
                  </Col>
                  <Col md={3} style={{ marginBottom: 12 }}>
                    <div style={metricCardStyle}>
                      <div style={metricLabelStyle}>Frete simulado</div>
                      <div style={{ ...metricValueStyle, fontSize: "0.96rem" }}>{formatCurrency(detailMetrics.freteSimulado)}</div>
                    </div>
                  </Col>
                </Row>
              </SectionCard>

              <Row>
                {/* Durante a busca global a concentração (que é da fila do card
                    ativo) não representa os resultados; some para não confundir. */}
                {isSearching ? null : (
                <Col lg={4} style={{ marginBottom: 12 }}>
                  <SectionCard
                    title="Concentração por transportadora"
                    description="Clique em uma linha para abrir só os CTes dessa transportadora."
                    action={loadingDetails ? <ClipLoader size={16} color={AUTOPILOT_COLORS.accent} /> : null}
                    style={{ height: "100%" }}
                  >
                    <div style={{ maxHeight: "54vh", overflowY: "auto", paddingRight: 4 }}>
                      {transportadoras.length === 0 && !loadingDetails ? (
                        <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.82rem" }}>Sem transportadoras para este recorte.</div>
                      ) : (
                        <Table size="sm" responsive style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th style={tableHeadCellStyle}>Transportadora</th>
                              <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Qtd</th>
                              {hideDivergencia ? null : (
                                <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Divergência</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {transportadoras.map((line) => (
                              <tr
                                key={line.IdTransportadora || line.transportadora}
                                onClick={() => handleTransportadoraClick(line)}
                                style={{
                                  cursor: "pointer",
                                  background:
                                    getTransportadoraKey(line) === selectedTransportadoraKey
                                      ? "var(--everlog-card-surface-active)"
                                      : "transparent",
                                }}
                              >
                                <td style={tableCellStyle}>{line.transportadora || "-"}</td>
                                <td style={{ ...tableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>{line.quantidade ?? 0}</td>
                                {hideDivergencia ? null : (
                                  <td style={{ ...tableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                    {(() => {
                                      const delta = calcDivergencia(line.totalFreteReceber, line.totalFreteSimulado);
                                      if (delta === null) return "-";
                                      const color = delta > 0 ? "var(--everlog-status-danger)" : delta < 0 ? "var(--everlog-status-success)" : AUTOPILOT_COLORS.muted;
                                      return <span style={{ color, fontWeight: 600 }}>{formatCurrency(delta)}</span>;
                                    })()}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </div>
                  </SectionCard>
                </Col>
                )}

                <Col lg={isSearching ? 12 : 8} style={{ marginBottom: 12 }}>
                  <SectionCard
                    title="CTe detalhados"
                    description={
                      isSearching
                        ? "Resultados da busca em todas as etapas do período; a coluna Etapa mostra onde cada CT-e está."
                        : selectedTransportadora
                        ? `Exibindo apenas os CTes de ${selectedTransportadora.transportadora || "transportadora selecionada"}.`
                        : "Use a busca e o botão Filtros no topo para refinar a fila sem sair da auditoria."
                    }
                    action={
                      !isSearching && selectedTransportadora ? (
                        <Button color="link" size="sm" onClick={() => setSelectedTransportadoraKey(null)}>
                          Limpar transportadora
                        </Button>
                      ) : null
                    }
                  >
                    <div style={{ marginBottom: "0.65rem", display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                      <Badge color="info">
                        {isSearching
                          ? `${filteredDetails.length} CTe encontrado(s)`
                          : `${filteredDetails.length} CTe visível(is)`}
                      </Badge>
                      {isSearching ? (
                        <Badge color="warning">Busca em todas as etapas</Badge>
                      ) : (
                        <Badge color="secondary">{details.length} carregado(s)</Badge>
                      )}
                      {!isSearching && selectedTransportadora ? (
                        <Badge color="primary">{selectedTransportadora.transportadora || "Transportadora selecionada"}</Badge>
                      ) : null}
                      {bulkSelectable ? (
                        <div style={{ marginLeft: "auto", display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                          {bulkFeedback ? (
                            <span
                              style={{
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                color:
                                  bulkFeedback.type === "success"
                                    ? "var(--everlog-status-success)"
                                    : "var(--everlog-status-danger)",
                              }}
                            >
                              {bulkFeedback.message}
                            </span>
                          ) : null}
                          <Button
                            color="secondary"
                            className="btn-sm"
                            disabled={bulkSaving || selectedCteIds.size === 0 || selectedCteIds.size > MAX_CTES_POR_ENVIO}
                            onClick={abrirPedirAnalise}
                            title={
                              selectedCteIds.size > MAX_CTES_POR_ENVIO
                                ? `Máximo de ${MAX_CTES_POR_ENVIO} por envio`
                                : "Envia um e-mail por transportadora pedindo a explicação do cálculo dos CTes selecionados (a transportadora não vê o valor pela nossa tabela)"
                            }
                          >
                            {`Pedir análise à transportadora (${selectedCteIds.size})`}
                          </Button>
                          <Button
                            color="success"
                            className="btn-sm"
                            disabled={bulkSaving || selectedCteIds.size === 0}
                            onClick={handleAprovarSelecionados}
                            title="Marca os CTes selecionados como OK (aprovação manual da divergência)"
                          >
                            {bulkSaving ? (
                              <ClipLoader size={12} color="#fff" />
                            ) : (
                              `Aprovar selecionados (${selectedCteIds.size})`
                            )}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                    <div style={{ maxHeight: "58vh", overflowY: "auto" }}>
                      <Table size="sm" responsive style={{ marginBottom: 0 }}>
                        <thead>
                          <tr>
                            {bulkSelectable ? (
                              <th style={{ ...tableHeadCellStyle, width: 34 }}>
                                <input
                                  type="checkbox"
                                  checked={allVisibleSelected}
                                  onChange={toggleSelectAllVisible}
                                  disabled={!selectableIds.length}
                                  title="Selecionar todos os CTes visíveis"
                                  style={{ cursor: "pointer" }}
                                />
                              </th>
                            ) : null}
                            <th style={tableHeadCellStyle}>Documento</th>
                            {isSearching ? <th style={tableHeadCellStyle}>Etapa</th> : null}
                            <th style={tableHeadCellStyle}>Série</th>
                            <th style={tableHeadCellStyle}>Transportadora</th>
                            <th style={tableHeadCellStyle}>Fatura</th>
                            <th style={tableHeadCellStyle}>Data emissão</th>
                            <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Frete receber</th>
                            <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Frete simulado</th>
                            {hideDivergencia ? null : (
                              <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Divergência</th>
                            )}
                            <th style={tableHeadCellStyle}>Chave</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(isSearching ? loadingGlobalSearch : loadingDetails) ? (
                            <tr>
                              <td colSpan={(isSearching ? 10 : 9) + (bulkSelectable ? 1 : 0) - (hideDivergencia ? 1 : 0)} style={{ ...tableCellStyle, textAlign: "center", padding: "1.2rem" }}>
                                <ClipLoader size={18} color={AUTOPILOT_COLORS.accent} />
                              </td>
                            </tr>
                          ) : filteredDetails.length === 0 ? (
                            <tr>
                              <td colSpan={(isSearching ? 10 : 9) + (bulkSelectable ? 1 : 0) - (hideDivergencia ? 1 : 0)} style={{ ...tableCellStyle, textAlign: "center", padding: "1.2rem" }}>
                                {isSearching
                                  ? (
                                      <AvisoForaDoFiltro
                                        tipo="cte"
                                        tela="auditoria"
                                        termo={detailsSearch}
                                        contexto={{ embarcadorId: empresaId, transportadoraId, dataInicial, dataFinal }}
                                        fallback="Nenhum CTe encontrado para a busca no período selecionado."
                                      />
                                    )
                                  : selectedTransportadora
                                  ? "Nenhum CTe encontrado para a transportadora selecionada."
                                  : "Nenhum CTe encontrado para o recorte atual."}
                              </td>
                            </tr>
                          ) : (
                            filteredDetails.map((item) => (
                              <tr
                                key={item.Id}
                                onClick={() => handleOpenCteDetail(item)}
                                style={{ cursor: "pointer" }}
                              >
                                {bulkSelectable ? (
                                  <td
                                    style={tableCellStyle}
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedCteIds.has(item.Id)}
                                      onChange={() => toggleCteSelection(item.Id)}
                                      style={{ cursor: "pointer" }}
                                    />
                                  </td>
                                ) : null}
                                <td style={tableCellStyle}>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenCteDetail(item);
                                    }}
                                    style={{
                                      border: 0,
                                      background: "transparent",
                                      color: AUTOPILOT_COLORS.accent,
                                      fontWeight: 700,
                                      padding: 0,
                                      textDecoration: "underline",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {item.numero || item.Id}
                                  </button>
                                  {isCanceladoSefaz(item) ? (
                                    <span style={{ marginLeft: "0.5rem" }}>
                                      <CanceladoSefazBadge compact />
                                    </span>
                                  ) : null}
                                  {hideDivergencia && isAprovadoManualmente(item) ? (
                                    <span style={{ marginLeft: "0.5rem" }}>
                                      <AprovadoManualmenteBadge
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          setAprovacaoModal(item);
                                        }}
                                      />
                                    </span>
                                  ) : null}
                                </td>
                                {isSearching ? (
                                  <td style={{ ...tableCellStyle, whiteSpace: "nowrap" }}>
                                    {(() => {
                                      const bucket = getStatusBucket(item);
                                      const bucketMeta = bucket ? STATUS_META[bucket] : null;
                                      if (!bucketMeta) return "-";
                                      return (
                                        <span
                                          style={{
                                            display: "inline-block",
                                            borderRadius: 999,
                                            padding: "0.18rem 0.55rem",
                                            fontSize: "0.71rem",
                                            fontWeight: 600,
                                            color: bucketMeta.color,
                                            border: `1px solid ${bucketMeta.color}`,
                                            background: `color-mix(in srgb, ${bucketMeta.color} 13%, transparent)`,
                                          }}
                                        >
                                          {bucketMeta.title}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                ) : null}
                                <td style={{ ...tableCellStyle, whiteSpace: "nowrap" }}>{item.serie || "-"}</td>
                                <td style={tableCellStyle}>{item.transportadora || "-"}</td>
                                <td style={{ ...tableCellStyle, whiteSpace: "nowrap" }}><FaturaDoCteCelula fatura={item.fatura} onAbrir={abrirFatura} /></td>
                                <td style={tableCellStyle}>{formatDateLabel(item.dataEmissao)}</td>
                                <td style={{ ...tableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>{formatCurrency(item.valorFreteReceber)}</td>
                                <td style={{ ...tableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>{formatCurrency(item.valorFreteSimulado)}</td>
                                {hideDivergencia ? null : (
                                <td style={{ ...tableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                  {(() => {
                                    const delta = calcDivergencia(item.valorFreteReceber, item.valorFreteSimulado);
                                    if (delta === null) return "-";
                                    const color = delta > 0 ? "var(--everlog-status-danger)" : delta < 0 ? "var(--everlog-status-success)" : AUTOPILOT_COLORS.muted;
                                    return (
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                                        {isAprovadoManualmente(item) ? (
                                          <AprovadoManualmenteBadge
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              setAprovacaoModal(item);
                                            }}
                                          />
                                        ) : null}
                                        <span style={{ color, fontWeight: 600 }}>{formatCurrency(delta)}</span>
                                      </span>
                                    );
                                  })()}
                                </td>
                                )}
                                <td style={{ ...tableCellStyle, whiteSpace: "nowrap" }}>
                                  <span title={item.chaveAcesso} style={{ cursor: "help", fontFamily: "monospace", fontSize: "0.78rem" }}>
                                    {formatChaveAcesso(item.chaveAcesso)}
                                  </span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </SectionCard>
                </Col>
              </Row>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Pedir análise à transportadora (PO1-3391) */}
      {pedirAnalise ? (
        <PedirAnaliseModal
          isOpen
          onClose={() => setPedirAnalise(null)}
          embarcadorId={empresaId}
          ids={pedirAnalise.ids}
          itens={pedirAnalise.itens}
          onEnviado={handlePedidoEnviado}
        />
      ) : null}

      {/* Modal pequeno do badge "Aprovado manualmente" (PO1-1801) */}
      <Modal
        isOpen={!!aprovacaoModal}
        toggle={() => setAprovacaoModal(null)}
        size="sm"
        modalClassName="modal-align-top"
        backdropClassName="cte-modal-backdrop"
        fade={false}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "0.8rem",
            padding: "1rem 1.25rem 0.75rem",
            borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
            background: "var(--everlog-card-hero-bg)",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={autopilotEyebrowStyle}>Aprovação manual</div>
            <div style={{ ...autopilotTitleStyle, fontSize: "1.1rem", marginBottom: "0.15rem" }}>
              {rotuloDocumento(aprovacaoModal && aprovacaoModal.modelo)}{" "}
              {aprovacaoModal ? aprovacaoModal.numero || aprovacaoModal.Id : "-"}
            </div>
          </div>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setAprovacaoModal(null)}
            style={detailModalCloseButtonStyle}
          >
            ×
          </button>
        </div>
        <ModalBody
          style={{
            background: "var(--everlog-card-surface)",
            color: AUTOPILOT_COLORS.text,
            padding: "1rem 1.25rem 1.25rem",
          }}
        >
          {aprovacaoModal ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              <div style={{ fontSize: "0.82rem" }}>
                <div style={{ color: AUTOPILOT_COLORS.muted }}>Aprovado por</div>
                <div style={{ fontWeight: 600 }}>
                  {(aprovacaoModal.ultimaModificacaoPorNome || "").trim() ||
                    (aprovacaoModal.ultimaModificacaoPorId
                      ? `Usuário ${aprovacaoModal.ultimaModificacaoPorId}`
                      : "Não identificado")}
                </div>
              </div>
              <div style={{ fontSize: "0.82rem" }}>
                <div style={{ color: AUTOPILOT_COLORS.muted }}>Data e hora</div>
                <div style={{ fontWeight: 600 }}>
                  {aprovacaoModal.ultimaModificacaoEm
                    ? new Date(aprovacaoModal.ultimaModificacaoEm).toLocaleString("pt-BR")
                    : "—"}
                </div>
              </div>
              <div style={{ fontSize: "0.82rem", display: "flex", gap: "1.2rem" }}>
                <div>
                  <div style={{ color: AUTOPILOT_COLORS.muted }}>Divergência</div>
                  <div style={{ fontWeight: 600, color: "var(--everlog-status-danger)" }}>
                    {formatCurrency(
                      calcDivergencia(aprovacaoModal.valorFreteReceber, aprovacaoModal.valorFreteSimulado)
                    )}
                  </div>
                </div>
                <div>
                  <div style={{ color: AUTOPILOT_COLORS.muted }}>Divergência aceitável</div>
                  <div style={{ fontWeight: 600 }}>
                    {aprovacaoModal.divergenciaAceitavel != null
                      ? formatCurrency(aprovacaoModal.divergenciaAceitavel)
                      : "—"}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={detailModalOpen}
        toggle={handleCloseDetail}
        size="xl"
        modalClassName="modal-align-top cte-audit-detail-modal"
        fade={false}
        autoFocus={false}
      >
        <div className="cte-audit-detail-header" style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "0.8rem",
          padding: "1rem 1.25rem 0.75rem",
          borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
          background: "var(--everlog-card-hero-bg)",
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={autopilotEyebrowStyle}>Detalhe do {rotuloDocumento(detailSummary && detailSummary.modelo)}</div>
            <div style={{ ...autopilotTitleStyle, fontSize: "1.1rem", marginBottom: "0.15rem" }}>
              {detailSummary
                ? `${detailSummary.numero || "-"}${detailSummary.serie ? ` / ${detailSummary.serie}` : ""}`
                : "Documento não identificado"}
            </div>
          </div>
          <div className="cte-audit-detail-header__actions" style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
            {detailSummary ? <VerXmlCteButton summary={detailSummary} /> : null}
            {detailSummary ? (
              <RecalcularFreteCteButton
                summary={detailSummary}
                simulado={detailAuditoriaFinanceira?.resumo?.totalSimulado ?? detailSummary.valorFreteSimulado}
                bloqueio={detailFatura?.bloqueioRecalculo}
                onRecalculated={handleRecalculatedCte}
              />
            ) : null}
            {detailModalClose}
          </div>
        </div>
        <ModalBody
          className="cte-audit-detail-body"
          style={{
            background: "var(--everlog-card-surface)",
            color: AUTOPILOT_COLORS.text,
            padding: "1rem 1rem 1.25rem",
            maxHeight: "82vh",
            overflowY: "auto",
          }}
        >
          <ErrorBanner message={detailError} />

          {detailLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "2rem 0 2.4rem" }}>
              <ClipLoader size={24} color={AUTOPILOT_COLORS.accent} />
            </div>
          ) : detailSummary ? (
            <>
              <div className="cte-audit-detail-diagnosis" style={detailDiagnosisCardStyle(detailDiagnosisMeta)}>
                <div style={detailDiagnosisBadgeStyle(detailDiagnosisMeta)}>
                  {detailDiagnosisMeta.badge}
                </div>
                <div style={{ ...detailDiagnosisTitleStyle, color: detailDiagnosisMeta.color }}>
                  {detailDiagnosis.title}
                </div>
                <div style={detailDiagnosisDescriptionStyle}>{detailDiagnosis.description}</div>

                {isCanceladoSefaz(detailSummary) && detailDiagnosis.id !== "cancelado_sefaz" ? (
                  // PO1-3071: mesmo alerta do componente compartilhado. Esta tela tem
                  // modal proprio, entao a faixa precisa existir aqui tambem.
                  // QA 13/09: quando o proprio diagnostico JA e o de cancelamento,
                  // a faixa repetiria o mesmo texto logo abaixo dele. Ela fica para os
                  // casos em que o diagnostico e outro (ex.: diferenca financeira), para
                  // o cancelamento nao passar batido.
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.6rem",
                      flexWrap: "wrap",
                      margin: "0.85rem 0 0",
                      padding: "0.6rem 0.75rem",
                      borderRadius: 10,
                      background: "var(--everlog-status-danger-bg)",
                      border: "1px solid var(--everlog-status-danger-border)",
                    }}
                  >
                    <CanceladoSefazBadge />
                    <span style={{ fontSize: "0.82rem", color: AUTOPILOT_COLORS.text }}>
                      {CANCELADO_SEFAZ_TITLE}
                    </span>
                  </div>
                ) : null}

                <div
                  className="cte-audit-detail-metrics"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: "0.75rem",
                  }}
                >
                  <DetailMetric
                    label="Frete a receber"
                    value={formatCurrency(detailSummary.valorFreteReceber)}
                    color="var(--everlog-card-title)"
                  />
                  <DetailMetric
                    label="Simulado"
                    value={formatCurrency(detailAuditoriaFinanceira?.resumo?.totalSimulado ?? detailSummary.valorFreteSimulado)}
                    color="var(--everlog-status-info)"
                  />
                  <DetailMetric
                    label="Frete combinado"
                    value={formatCurrency(detailSummary.valorFreteCombinado)}
                    color="var(--everlog-status-info)"
                  />
                  <DetailMetric
                    label="Diferença"
                    value={formatCurrency(detailAuditoriaFinanceira?.resumo?.diferenca ?? detailSummary.valorDivergencia)}
                    color={detailFinancialDifferenceColor}
                  />
                </div>
              </div>

              <div className="cte-audit-detail-tabs" style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                {detailTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setDetailTab(tab.id)}
                    className={`cte-audit-detail-tab${detailTab === tab.id ? " is-active" : ""}`}
                    style={detailTabStyle(detailTab === tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {detailTab === "visao-geral" ? (
                <>
                  <SectionCard
                    style={{ ...overviewSectionCardStyle, marginBottom: "1rem" }}
                  >
                    <Row>
                      <Col lg={7} style={{ marginBottom: 12 }}>
                        <div style={overviewPanelStyle}>
                          <div style={overviewPanelEyebrowStyle}>Cadastro</div>
                          <div style={overviewPanelTitleStyle}>Identificação e contexto</div>
                          <div style={overviewPanelDescriptionStyle}>
                            Dados-base do documento para reconhecer rapidamente {detailDoc.o} e seu contexto administrativo.
                          </div>

                          <div className="cte-audit-detail-party-summary">
                            <div className="cte-audit-detail-party-summary__field cte-audit-detail-party-summary__field--main">
                              <div style={overviewFieldLabelStyle}>Transportadora</div>
                              <div style={overviewFieldMutedValueStyle}>
                                {detailPartes?.transportadora?.fantasia || detailSummary.transportadora || "-"}
                              </div>
                            </div>
                            <div className="cte-audit-detail-party-summary__field">
                              <div style={overviewFieldLabelStyle}>CNPJ/CPF</div>
                              <div style={overviewFieldMutedValueStyle}>{detailPartes?.transportadora?.cnpjCpf || "-"}</div>
                            </div>
                            <div className="cte-audit-detail-party-summary__field">
                              <div style={overviewFieldLabelStyle}>Localidade</div>
                              <div style={overviewFieldMutedValueStyle}>
                                {buildLocationDisplay(detailPartes?.transportadora?.cidade, detailPartes?.transportadora?.uf)}
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                              gap: "0.75rem",
                              marginBottom: "1rem",
                            }}
                          >
                            <div style={overviewMiniMetricStyle}>
                              <div style={overviewFieldLabelStyle}>Emissão</div>
                              <div style={overviewFieldMutedValueStyle}>{formatDateLabel(detailSummary.dataEmissao)}</div>
                            </div>
                            <div style={overviewMiniMetricStyle}>
                              <div style={overviewFieldLabelStyle}>Modal</div>
                              <div style={overviewFieldMutedValueStyle}>{detailSummary.modal || "-"}</div>
                            </div>
                            <div style={overviewMiniMetricStyle}>
                              <div style={overviewFieldLabelStyle}>Tipo do documento</div>
                              <div style={overviewFieldMutedValueStyle}>
                                {ehNfse(detailSummary.modelo)
                                  ? "NFse"
                                  : detailSummary.tipoConhecimento || detailSummary.tipoCte || "-"}
                              </div>
                            </div>
                            <div style={overviewMiniMetricStyle}>
                              <div style={overviewFieldLabelStyle}>Fatura</div>
                              <div style={overviewFieldMutedValueStyle}><FaturaDoCteResumo fatura={detailFatura} onAbrir={abrirFatura} /></div>
                            </div>
                            <div style={overviewMiniMetricStyle}>
                              <div style={overviewFieldLabelStyle}>Pago</div>
                              <div style={overviewFieldMutedValueStyle}>{formatBooleanLabel(detailSummary.pago)}</div>
                            </div>
                            {detailSummary.idAglutinacaoNotaFiscal ? (
                              <div
                                style={{
                                  ...overviewMiniMetricStyle,
                                  borderColor: "var(--everlog-status-danger-border)",
                                  background: "var(--everlog-status-danger-bg)",
                                }}
                              >
                                <div style={overviewFieldLabelStyle}>Aglutinação</div>
                                <div style={{ ...overviewFieldValueStyle, color: "var(--everlog-status-danger)" }}>
                                  #{detailSummary.idAglutinacaoNotaFiscal}
                                </div>
                              </div>
                            ) : null}
                          </div>

                          <div style={overviewFieldGridStyle}>
                            <div style={{ ...overviewFieldStyle, gridColumn: "1 / -1" }}>
                              <div style={overviewKeyHeaderStyle}>
                                <div style={overviewFieldLabelStyle}>Chave de acesso</div>
                                <button
                                  type="button"
                                  id="copy-cte-access-key"
                                  onClick={handleCopyDetailAccessKey}
                                  style={overviewCopyButtonStyle}
                                >
                                  Copiar
                                </button>
                                <UncontrolledTooltip
                                  placement="top"
                                  target="copy-cte-access-key"
                                  delay={{ show: 150, hide: 0 }}
                                >
                                  {detailKeyCopyState === "copied"
                                    ? "Chave copiada"
                                    : detailKeyCopyState === "error"
                                      ? "Não foi possível copiar"
                                      : "Copiar chave completa"}
                                </UncontrolledTooltip>
                              </div>
                              <div
                                style={{
                                  ...overviewFieldMutedValueStyle,
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                {detailSummary.chaveAcesso || "-"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>

                      <Col lg={5} style={{ marginBottom: 12 }}>
                        <div style={overviewPanelStyle}>
                          <div style={overviewPanelEyebrowStyle}>Operação</div>
                          <div style={overviewPanelTitleStyle}>Panorama operacional</div>
                          <div style={overviewPanelDescriptionStyle}>
                            Origem, destino, entrega e volume associado ao documento.
                          </div>

                          <div style={overviewRouteCardStyle}>
                            <div style={overviewRouteLabelStyle}>Rota principal</div>
                            <div style={overviewRouteValueStyle}>{detailOriginLabel} → {detailDestinationLabel}</div>
                          </div>

                          <div style={overviewMiniMetricGridStyle}>
                            <div style={overviewMiniMetricStyle}>
                              <div style={overviewFieldLabelStyle}>Peso do documento</div>
                              <div style={overviewFieldMutedValueStyle}>{formatWeight(detailSummary.pesoCte)}</div>
                            </div>
                            <div style={overviewMiniMetricStyle}>
                              <div style={overviewFieldLabelStyle}>Entrega prevista</div>
                              <div style={overviewFieldMutedValueStyle}>{formatDateLabel(detailSummary.dataPrevistaEntrega)}</div>
                            </div>
                            <div style={overviewMiniMetricStyle}>
                              <div style={overviewFieldLabelStyle}>Entrega simulada</div>
                              <div style={overviewFieldMutedValueStyle}>{formatDateLabel(detailSummary.dataPrometidaEntrega)}</div>
                            </div>
                            <div style={overviewMiniMetricStyle}>
                              <div style={overviewFieldLabelStyle}>Peso NFes</div>
                              <div style={overviewFieldMutedValueStyle}>{formatWeight(detailSummary.pesoTotal)}</div>
                            </div>
                            <div style={overviewMiniMetricStyle}>
                              <div style={overviewFieldLabelStyle}>Mercadoria</div>
                              <div style={overviewFieldMutedValueStyle}>{formatCurrency(detailSummary.valorTotalMercadoria)}</div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </SectionCard>
                </>
              ) : null}

              {detailTab === "auditoria" ? (
                <>
                  <SectionCard
                    title="Consistência da base do cálculo"
                    description="Comece pelos indicadores principais para entender se o caso é divergência real, ausência de dado ou apenas diferença pontual."
                    style={{ marginBottom: "1rem" }}
                  >
                    <Row>
                      <Col md={4} style={{ marginBottom: 12 }}>
                        <DetailMetric label="Checks OK" value={detailAuditoria?.resumo?.ok || 0} color="var(--everlog-status-success)" />
                      </Col>
                      <Col md={4} style={{ marginBottom: 12 }}>
                        <DetailMetric
                          label="Divergências"
                          value={detailAuditoria?.resumo?.divergente || 0}
                          color="var(--everlog-status-danger)"
                        />
                      </Col>
                      <Col md={4} style={{ marginBottom: 12 }}>
                        <DetailMetric
                          label="Sem dado"
                          value={detailAuditoria?.resumo?.semDado || 0}
                          color="var(--everlog-status-warning)"
                        />
                      </Col>
                    </Row>
                  </SectionCard>

                  <SectionCard
                    title="Checks da composição"
                    description="Valide peso, valor de mercadoria, quantidade de NFes e origem/destino antes de aprofundar na comparação financeira."
                  >
                    <Table size="sm" responsive style={{ marginBottom: 0 }}>
                      <colgroup>
                        <col style={{ width: "23%" }} />
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "14%" }} />
                        <col style={{ width: "12%" }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th style={tableHeadCellStyle}>Regra</th>
                          <th style={tableHeadCellStyle}>Documento</th>
                          <th style={tableHeadCellStyle}>NFes vinculadas</th>
                          <th style={tableHeadCellStyle}>Diferença</th>
                          <th style={tableHeadCellStyle}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailAuditoria?.checks?.length ? (
                          detailAuditoria.checks.map((check) => (
                            <tr key={check.id}>
                              <td
                                style={{
                                  ...detailTableCellStyle,
                                  fontWeight: 700,
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                {check.label || "-"}
                              </td>
                              <td style={{ ...detailTableCellStyle, overflowWrap: "anywhere", wordBreak: "break-word" }}>
                                {formatAuditValue(
                                  check.cteValor,
                                  check.id === "peso"
                                    ? "peso"
                                    : check.id === "valor_mercadoria"
                                      ? "currency"
                                      : null
                                )}
                              </td>
                              <td style={{ ...detailTableCellStyle, overflowWrap: "anywhere", wordBreak: "break-word" }}>
                                {check.id === "peso" ? (
                                  <AuditWeightCell check={check} />
                                ) : (
                                  formatAuditValue(
                                    check.nfesValor,
                                    check.id === "valor_mercadoria" ? "currency" : null
                                  )
                                )}
                              </td>
                              <td style={{ ...detailTableCellStyle, whiteSpace: "nowrap" }}>
                                {formatAuditValue(
                                  check.diferenca,
                                  check.id === "peso"
                                    ? "peso"
                                    : check.id === "valor_mercadoria"
                                      ? "currency"
                                      : null
                                )}
                              </td>
                              <td style={detailTableCellStyle}>
                                <AuditStatusBadge status={check.status} />
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" style={{ ...detailTableCellStyle, textAlign: "center", padding: "1rem" }}>
                              Nenhum check de auditoria disponível para {detailDoc.este}.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </SectionCard>

                  <SectionCard
                    title="Composição financeira"
                    description="Compare o total a receber com o total simulado e valide os grupos que participam da composição antes de aprofundar nos itens."
                    style={{ marginTop: "1rem" }}
                  >
                    <Row>
                      <Col md={8}>
                        <DetailField
                          label="Tabela de preço"
                          value={
                            <SimulatedPriceTablesValue
                              summary={detailAuditoriaFinanceira?.tabelaPrecoSimulado}
                              onOpenTabela={podeAbrirTabelaFrete(user) ? setTabelaFreteAbertaId : null}
                            />
                          }
                          valueStyle={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                        />
                      </Col>
                      <Col md={4}>
                        <DetailField label="Limite de divergência" value={<LimiteDivergenciaValue summary={detailSummary} />} />
                      </Col>
                    </Row>

                    <Table size="sm" responsive style={{ marginBottom: 0 }}>
                      <colgroup>
                        <col style={{ width: "26%" }} />
                        <col style={{ width: "20%" }} />
                        <col style={{ width: "20%" }} />
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "12%" }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th style={tableHeadCellStyle}>Grupo</th>
                          <th style={tableHeadCellStyle}>Documento</th>
                          <th style={tableHeadCellStyle}>Simulado</th>
                          <th style={tableHeadCellStyle}>Diferença</th>
                          <th style={tableHeadCellStyle}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailFinancialGroups.length ? (
                          detailFinancialGroups.map((check) => {
                            // total_simulado é uma comparação própria, não a soma das linhas
                            // acima: separa com borda/espaço para não parecer somatório. [PO1-2491]
                            const isTotal = check.id === "total_simulado";
                            const cellStyle = isTotal
                              ? {
                                  ...detailTableCellStyle,
                                  borderTop: `2px solid ${AUTOPILOT_COLORS.dividerStrong}`,
                                  paddingTop: "0.65rem",
                                }
                              : detailTableCellStyle;
                            return (
                              <tr key={check.id}>
                                <td style={{ ...cellStyle, fontWeight: 700 }}>
                                  {check.label || "-"}
                                  {check.id === "tributos" ? (
                                    <TributoIncideBadge incide={check.tributoIncideNoTotal} tipo={check.tributoTipo} />
                                  ) : null}
                                </td>
                                <td style={cellStyle}>
                                  {formatAuditValue(check.cteValor, "currency")}
                                  {check.id === "tributos" && check.tributoOrigemCte === "componente" ? (
                                    <div
                                      title={`${detailDoc.O} destaca o imposto como componente do frete; esse valor entra aqui e sai de Taxas.`}
                                      style={{ fontSize: "0.72rem", color: "var(--everlog-card-muted)", marginTop: 2 }}
                                    >
                                      componente: {(check.tributoComponentesCte || []).join(" / ")}
                                    </div>
                                  ) : null}
                                </td>
                                <td style={cellStyle}>{formatAuditValue(check.nfesValor, "currency")}</td>
                                <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                                  {formatAuditValue(check.diferenca, "currency")}
                                </td>
                                <td style={cellStyle}>
                                  <AuditStatusBadge status={check.status} />
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="5" style={{ ...detailTableCellStyle, textAlign: "center", padding: "1rem" }}>
                              Nenhum comparativo financeiro disponível para {detailDoc.este}.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </SectionCard>

                  <SectionCard
                    title={`Componentes ${detailDoc.do} x simulado`}
                    description="Compara os componentes recebidos no documento com a regra e a memória de cálculo do simulador. Itens sem de/para permanecem visíveis."
                    style={{ marginTop: "1rem" }}
                  >
                    <FinancialComponentComparisonTable
                      rows={detailAuditoriaFinanceira?.componentesComparativo || detailAuditoriaFinanceira?.taxas?.itens}
                      formatValue={formatAuditValue}
                      headCellStyle={tableHeadCellStyle}
                      cellStyle={detailTableCellStyle}
                      onLinkComponent={handleLinkComponent}
                    />
                  </SectionCard>
                </>
              ) : null}

              {detailTab === "notas" ? (
                <SectionCard
                  title="Notas fiscais vinculadas"
                  description={`Use esta aba para entender a composição ${detailDoc.do} e localizar a origem de uma divergência sem sair do mesmo contexto.`}
                >
                  <div style={{ marginBottom: "0.65rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <Badge color="info">{detailNfs.length} NFe vinculada(s)</Badge>
                  </div>
                  <div style={{ maxHeight: "46vh", overflowY: "auto" }}>
                    <Table size="sm" responsive style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th style={tableHeadCellStyle}>NFe</th>
                          <th style={tableHeadCellStyle}>Série</th>
                          <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Peso</th>
                          <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Valor total</th>
                          <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Frete simulado</th>
                          <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Frete combinado</th>
                          <th style={tableHeadCellStyle}>Entrega</th>
                          <th style={tableHeadCellStyle}>Entrega simulada</th>
                          <th style={tableHeadCellStyle}>Pedido</th>
                          <th style={tableHeadCellStyle}>CNPJ remetente</th>
                          <th style={tableHeadCellStyle}>Negócio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailNfs.length === 0 ? (
                          <tr>
                            <td colSpan="11" style={{ ...detailTableCellStyle, textAlign: "center", padding: "1rem" }}>
                              Nenhuma NFe vinculada encontrada para {detailDoc.este}.
                            </td>
                          </tr>
                        ) : (
                          detailNfs.map((nota) => (
                            <tr key={nota.id}>
                              <td style={detailTableCellStyle}>
                                {nota.id && nota.numero ? (
                                  <button type="button" style={nfLinkStyle} title="Ver detalhe da NFe" onClick={() => setNfAbertaId(nota.id)}>
                                    {nota.numero}
                                  </button>
                                ) : (
                                  nota.numero || "-"
                                )}
                              </td>
                              <td style={detailTableCellStyle}>{nota.serie || "-"}</td>
                              <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>{formatWeight(nota.peso)}</td>
                              <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>{formatCurrency(nota.valorTotal)}</td>
                              <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>{formatCurrency(nota.valorFreteSimulado)}</td>
                              <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>{formatCurrency(nota.valorFreteCombinado)}</td>
                              <td style={detailTableCellStyle}>{formatDateLabel(nota.dataEntrega)}</td>
                              <td style={detailTableCellStyle}>{formatDateLabel(nota.dataEntregaSimulada)}</td>
                              <td style={detailTableCellStyle}>{nota.pedido || "-"}</td>
                              <td style={detailTableCellStyle}>{nota.cnpjRemetente || "-"}</td>
                              <td style={detailTableCellStyle}>{nota.negocio || "-"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </SectionCard>
              ) : null}

              {detailTab === "fatura" ? (
                <SectionCard
                  title="Fatura"
                  description={`Fatura em que ${detailDoc.este} está, a situação dela e a decisão tomada na conferência.`}
                >
                  <FaturaDoCteAba fatura={detailFatura} onAbrir={abrirFatura} />
                </SectionCard>
              ) : null}

              {detailTab === "visao-geral" ? (
                <>
                  <Row>
                    <Col lg={12} style={{ marginBottom: 12 }}>
                      <SectionCard title="Remetente" description={`Dados cadastrais do remetente vinculados ${detailDoc.ao}.`}>
                        <Row>
                          <Col md={4}><DetailField label="CNPJ/CPF" value={detailPartes?.remetente?.cnpjCpf} /></Col>
                          <Col md={8}><DetailField label="Nome fantasia" value={detailPartes?.remetente?.fantasia} /></Col>
                        </Row>
                        <Row>
                          <Col md={9}><DetailField label="Cidade" value={detailPartes?.remetente?.cidade} /></Col>
                          <Col md={3}><DetailField label="UF" value={detailPartes?.remetente?.uf} /></Col>
                        </Row>
                      </SectionCard>
                    </Col>
                  </Row>

                  <Row>
                    <Col lg={12} style={{ marginBottom: 12 }}>
                      <SectionCard title="Componentes" description={`Componentes de prestação de serviço usados na composição ${detailDoc.do}.`}>
                        <Table size="sm" responsive style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th style={tableHeadCellStyle}>Nome</th>
                              <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Valor</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailComponentes.length === 0 ? (
                              <tr>
                                <td colSpan="2" style={{ ...detailTableCellStyle, textAlign: "center", padding: "1rem" }}>
                                  Nenhum componente encontrado para {detailDoc.este}.
                                </td>
                              </tr>
                            ) : (
                              detailComponentes.map((item) => (
                                <tr key={item.id}>
                                  <td style={detailTableCellStyle}>{item.nome || "-"}</td>
                                  <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                    {formatCurrency(item.valor)}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </SectionCard>
                    </Col>

                    <Col lg={12} style={{ marginBottom: 12 }}>
                      <SectionCard title="Impostos" description={`Leitura tributária ${detailDoc.do} para apoiar a conferência e a análise de cálculo.`}>
                        <Table size="sm" responsive style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th style={tableHeadCellStyle}>Nome</th>
                              <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Base cálculo ICMS</th>
                              <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Valor ICMS</th>
                              <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Alíquota</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailImpostos.length === 0 ? (
                              <tr>
                                <td colSpan="4" style={{ ...detailTableCellStyle, textAlign: "center", padding: "1rem" }}>
                                  Nenhum imposto encontrado para {detailDoc.este}.
                                </td>
                              </tr>
                            ) : (
                              detailImpostos.map((item) => (
                                <tr key={item.id}>
                                  <td style={detailTableCellStyle}>{item.nome || "-"}</td>
                                  <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                    {formatCurrency(item.valorBaseCalculoIcms)}
                                  </td>
                                  <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                    {formatCurrency(item.valorIcms)}
                                  </td>
                                  <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                    {formatPercent(item.aliquotaIcms)}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </SectionCard>
                    </Col>
                  </Row>

                  <Row>
                    <Col lg={12} style={{ marginBottom: 12 }}>
                      <SectionCard title="Carga" description="Quantidade de carga, unidade e medida usadas no documento.">
                        <Table size="sm" responsive style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th style={tableHeadCellStyle}>Nome</th>
                              <th style={tableHeadCellStyle}>Medida</th>
                              <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Quantidade</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailCarga.length === 0 ? (
                              <tr>
                                <td colSpan="3" style={{ ...detailTableCellStyle, textAlign: "center", padding: "1rem" }}>
                                  Nenhuma quantidade de carga encontrada para {detailDoc.este}.
                                </td>
                              </tr>
                            ) : (
                              detailCarga.map((item) => (
                                <tr key={item.id}>
                                  <td style={detailTableCellStyle}>{item.nome || "-"}</td>
                                  <td style={detailTableCellStyle}>{item.medida || "-"}</td>
                                  <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                    {item.quantidade != null ? Number(item.quantidade).toLocaleString("pt-BR") : "-"}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </SectionCard>
                    </Col>

                    <Col lg={12} style={{ marginBottom: 12 }}>
                      <SectionCard title="Informações complementares" description="Campos auxiliares úteis na conferência e no tratamento de exceções.">
                        <Row>
                          <Col md={12}><DetailField label="CFOP" value={detailAdicionais?.cfop} /></Col>
                        </Row>
                        <Row>
                          <Col md={12}><DetailField label="Produto predominante" value={detailAdicionais?.produtoPredominante} /></Col>
                        </Row>
                        <Row>
                          <Col md={12}><DetailField label="Outras características" value={detailAdicionais?.outrasCaracteristicas} /></Col>
                        </Row>
                      </SectionCard>
                    </Col>
                  </Row>
                </>
              ) : null}
            </>
          ) : (
            <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.84rem", padding: "0.25rem 0 0.75rem" }}>
              Nenhum detalhe disponível para {detailDoc.este}.
            </div>
          )}
        </ModalBody>
        {tabelaFreteAbertaId && (
          <TabelaFreteModal id={tabelaFreteAbertaId} onClose={() => setTabelaFreteAbertaId(null)} />
        )}
      </Modal>
      <NotaFiscalDetailModal
        isOpen={Boolean(nfAbertaId)}
        notaFiscalId={nfAbertaId}
        embarcadorId={empresaId}
        onClose={() => setNfAbertaId(null)}
      />
      <FaturaDetalheModal
        isOpen={Boolean(faturaAberta)}
        fatura={faturaAberta}
        onClose={() => setFaturaAberta(null)}
      />
    </div>
  );
}
